import { LoginResponse } from "../components/users/LoginPanel";
import axios, { AxiosResponse, AxiosError } from "axios";
import User, { Role } from "../models/User";
import { SessionCatalogs, SessionInfo } from "../models/SessionInfo";
import Customer from "../models/Customer";
import CustomerContact, { CreditCardInfo } from "../models/CustomerContact";
import { QCCode } from "../models/QCCode";
import { customerPaysByCreditCard } from "../utils/creditCardCustomer";
import Vendor from "../models/Vendor";
import VendorContact from "../models/VendorContact";
import Manufacturer from "../models/Manufacturer";
import ManufacturerContact from "../models/ManufacturerContact";
import Rfq from "../models/Rfq";
import {
  SamGovOpportunity,
  SamGovSearchParams,
  SamGovSearchResponse,
} from "../models/SamGovOpportunity";
import { RFQStatus } from "../models/RFQStatus";
import { VendorRequestType } from "../models/VendorRequestType";
import { RfqItem } from "../models/RfqItem";
import { RfqItemShoppingCandidate } from "../models/RfqItemShoppingCandidate";
import { ShoppingCandidateVendorResolution } from "../models/ShoppingCandidateVendorResolution";
import {
  ExtractedProductPageResult,
  ProductPageSnapshot,
} from "../models/ProductPageExtraction";
import {
  ExtractedOrderPageResult,
  OrderPagePdfUpload,
} from "../models/OrderPageExtraction";
import { GoogleShopSearchResults } from "../models/GoogleShopSearchResults";
import { ProductResults } from "../models/GoogleProductDetails";
import { VendorQuote } from "../models/VendorQuote";
import { SaveVendorQuoteResponse } from "./VendorQuoteResponses";
import QuoteHistory from "../models/QuoteHistory";
import Email from "../models/Email";
import { RfqHomework } from "../models/RfqHomework";
import { VendorQuoteRfqEmail } from "../models/VendorQuoteRfqEmail";
import PriceQuote, { PriceQuoteStatus } from "../models/PriceQuote";
import PurchaseOrder, {
  PurchaseOrderFieldConflict,
  PurchaseOrderResolutionChoice,
} from "../models/PurchaseOrder";
import { RfqItemNumber } from "../models/RfqItemNumber";
import { VendorItemNumber } from "../models/VendorItemNumber";
import { Fee } from "../models/Fee";
import {
  changedVendorQuoteScalars,
  feesChanged,
  notesChanged,
  vendorItemNumbersChanged,
} from "../components/rfqs/vendorQuoteSaveDiff";
import { CatalogType, CatalogTypeCategory } from "../models/CatalogType";
import ZenkitItem, { ZenkitItemInput } from "../models/ZenkitItem";
import ZenkitQueue from "../models/ZenkitQueue";
import { assertQuickBooksVendorLabelsPresent } from "../utils/quickBooksVendorLabel";
import { shippingMethodToQuickBooks } from "../components/DeliveryTypeAutocomplete";
import { FixedPriceType } from "../models/FixedPriceType";
import { MfgCocCompare } from "../models/MfgCocCompare";
import {
  ChatAttachmentContent,
  ChatConversation,
  ChatCreateConversationInput,
  ChatMessage,
  ChatMessagePage,
  ChatSendMessageInput,
  ChatThread,
  TeamsStatus,
} from "../models/Chat";
import {
  qbEstimateLineAmount,
  qbLineTotalPerUnit,
  getVendorPurchaseTotalForQuickBooks,
  qbPurchaseOrderUnitCost,
} from "../PricingUtils";

// const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://138.197.2.41/api";
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3000/api";

/**
 * Socket.IO origin (no /api path). Call from the browser so SPA served from the API
 * (e.g. http://localhost:4000) connects to the same host.
 */
export function getSocketBaseUrl(): string {
  if (
    typeof process.env.REACT_APP_SOCKET_URL === "string" &&
    process.env.REACT_APP_SOCKET_URL.length > 0
  ) {
    return process.env.REACT_APP_SOCKET_URL;
  }
  const api = process.env.REACT_APP_API_BASE_URL || "";
  if (/^https?:\/\//i.test(api)) {
    return api.replace(/\/api\/?$/, "");
  }
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return API_BASE_URL.replace(/\/api\/?$/, "");
}

/** @deprecated use getSocketBaseUrl() in browser code */
export const SOCKET_BASE_URL =
  process.env.REACT_APP_SOCKET_URL || API_BASE_URL.replace(/\/api\/?$/, "");

export function quickBooksRfqDisplayNumber(
  referenceNum: string | null | undefined
): string {
  const raw = (referenceNum ?? "").trim();
  if (!raw) return "";
  return raw.split("|")[0]?.trim() ?? raw;
}

export const http = axios.create();

/** Prefer plain-text API errors; never surface nginx/HTML gateway pages in the UI. */
export function apiErrorMessage(error: unknown, fallback: string): string {
  if (!(error instanceof AxiosError)) {
    return error instanceof Error && error.message ? error.message : fallback;
  }
  const status = error.response?.status;
  const data = error.response?.data;
  if (typeof data === "string") {
    const trimmed = data.trim();
    if (!trimmed) return fallback;
    if (/^</.test(trimmed) || /<html[\s>]/i.test(trimmed)) {
      if (status === 502 || status === 503 || status === 504) {
        return "Server temporarily unavailable. Please try again.";
      }
      return fallback;
    }
    return trimmed.length > 300 ? `${trimmed.slice(0, 300)}…` : trimmed;
  }
  if (
    data &&
    typeof data === "object" &&
    "message" in data &&
    typeof (data as { message: unknown }).message === "string"
  ) {
    return (data as { message: string }).message;
  }
  if (status === 502 || status === 503 || status === 504) {
    return "Server temporarily unavailable. Please try again.";
  }
  return fallback;
}

export const fetchSessionInfo = async (options?: {
  includeCatalogs?: boolean;
  includeRfqs?: boolean;
}) => {
  const params: Record<string, string> = {};
  if (options?.includeCatalogs === false) {
    params.includeCatalogs = "false";
  }
  if (options?.includeRfqs === false) {
    params.includeRfqs = "false";
  }
  const response = await http.get(API_BASE_URL + "/session", {
    params: Object.keys(params).length > 0 ? params : undefined,
  });
  const sessionInfo: SessionInfo = response.data;
  return sessionInfo;
};

/** Directory-lite vendors/customers/manufacturers for refresh revalidation. */
export const fetchSessionCatalogs = async (): Promise<SessionCatalogs> => {
  const response = await http.get(API_BASE_URL + "/session/catalogs", {
    skipGlassPane: true,
  } as Parameters<typeof http.get>[1]);
  return response.data as SessionCatalogs;
};

/** Background refresh for the work queue; skips the global loading overlay. */
export const refreshSessionRequestForQuotes = async (): Promise<Rfq[]> => {
  const response = await http.get<{ requestForQuotes: Rfq[] }>(
    API_BASE_URL + "/session/request-for-quotes",
    {
      skipGlassPane: true,
    } as Parameters<typeof http.get>[1]
  );
  return response.data.requestForQuotes;
};

export const getInvitation = async (invitationId: string) => {
  const response: AxiosResponse<User> = await http.get(
    API_BASE_URL + "/user/invite/" + invitationId
  );
  return response.data;
};

export const sendInvitation = async (
  firstName: string,
  lastName: string,
  email: string,
  role: Role,
  customerId?: number,
  phone?: string
) => {
  const response: AxiosResponse<User> = await http.post(
    API_BASE_URL + "/user/invite" + (customerId ? "/" + customerId : ""),
    { firstName, lastName, email, role, phone }
  );
  return response.data;
};

export const createUser = async (
  username: string,
  password: string,
  firstName: string,
  lastName: string,
  email: string,
  role: Role
) => {
  const response: AxiosResponse<User> = await http.post(API_BASE_URL + "/user", {
    username,
    password,
    firstName,
    lastName,
    email,
    role,
  });
  return response.data;
};

export const deleteUser = async (id: number): Promise<string> => {
  const response: AxiosResponse<string> = await http.delete(
    API_BASE_URL + "/user/" + id
  );
  return response.data;
};

export const adminSetUserPassword = async (
  userId: number,
  password: string
): Promise<string> => {
  const response: AxiosResponse<string> = await http.put(
    API_BASE_URL + "/user/" + userId + "/password",
    { password }
  );
  return response.data;
};

export const register = async (
  invitationId: string,
  username: string,
  firstName: string,
  lastName: string,
  pwd: string,
  email: string
) => {
  const response: AxiosResponse<LoginResponse> = await http.post(
    API_BASE_URL + "/user/register/" + invitationId,
    { username, firstName, lastName, pwd, email }
  );
  return response.data;
};

export const forgotPassword = async (username: string) => {
  await http.post(API_BASE_URL + "/login/forgot/pwd", { username });
};

export const forgotUserName = async (email: string) => {
  await http.post(API_BASE_URL + "/login/forgot/username", { email });
};

export const resetPassword = async (
  verificationCode: string,
  username: string,
  password: string
) => {
  try {
    await http.post(
      API_BASE_URL + "/resetpassword",
      null,
      getRequestConfig({ username, verificationCode, password })
    );
    return true;
  } catch {
    return false;
  }
};

export const changePassword = async (
  oldPassword: string,
  newPassword: string
) => {
  await http.put(API_BASE_URL + "/login/pwd", { oldPassword, newPassword });
};

export const changeForgottenPassword = async (
  forgottenPwdId: string,
  userName: string,
  pwd: string
) => {
  await http.put(API_BASE_URL + "/login/forgottenpwd", {
    forgottenPwdId,
    userName,
    pwd,
  });
};

const getRequestConfig = (params: object) => {
  return { params: params };
};

export const updateUser = async (user: User) => {
  const response: AxiosResponse<User> = await http.put(
    API_BASE_URL + "/user",
    user
  );
  return response.data;
};

export const login = async (username: string, pwd: string) => {
  const response = await http.post(API_BASE_URL + "/login", {
    username,
    pwd,
  });
  return response.data as LoginResponse;
};

export const createCustomer = async (customer: Customer) => {
  const response: AxiosResponse<Customer> = await http.post(
    API_BASE_URL + "/customer",
    customer
  );
  return response.data;
};

/** Server-side name search for autocompletes (bounded result set). */
export interface VendorsPagedResponse {
  vendors: Vendor[];
  total: number;
}

export interface GetVendorsPagedOptions {
  sortField?: string;
  sortDir?: "asc" | "desc";
  /** Serialized server-side; shape matches MUI `GridFilterModel`. */
  filterModel?: { items?: unknown[]; logicOperator?: string };
}

export const getVendorsPaged = async (
  skip: number,
  take: number,
  options?: GetVendorsPagedOptions
): Promise<VendorsPagedResponse> => {
  try {
    const params: Record<string, string | number> = { skip, take };
    if (options?.sortField) {
      params.sortField = options.sortField;
    }
    if (options?.sortDir) {
      params.sortDir = options.sortDir;
    }
    if (
      options?.filterModel &&
      Array.isArray(options.filterModel.items) &&
      options.filterModel.items.length > 0
    ) {
      params.filterModel = JSON.stringify(options.filterModel);
    }
    const response = await http.get<VendorsPagedResponse>(
      `${VENDORS_API_URL}/paged`,
      { params }
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        String(error.response?.data ?? "") || "Failed to load vendors"
      );
    }
    throw error;
  }
};

export const searchVendors = async (
  q: string,
  limit = 50,
  options?: { signal?: AbortSignal }
): Promise<Vendor[]> => {
  try {
    const response = await http.get<Vendor[]>(`${VENDORS_API_URL}/search`, {
      params: { q, limit },
      signal: options?.signal,
    });
    return response.data;
  } catch (error) {
    if ((error as { code?: string }).code === "ERR_CANCELED") {
      throw error;
    }
    if (error instanceof AxiosError) {
      throw new Error(
        String(error.response?.data ?? "") || "Failed to search vendors"
      );
    }
    throw error;
  }
};

export const searchManufacturers = async (
  q: string,
  limit = 50,
  options?: { signal?: AbortSignal }
): Promise<Manufacturer[]> => {
  try {
    const response = await http.get<Manufacturer[]>(
      `${MANUFACTURERS_API_URL}/search`,
      {
        params: { q, limit },
        signal: options?.signal,
      }
    );
    return response.data;
  } catch (error) {
    if ((error as { code?: string }).code === "ERR_CANCELED") {
      throw error;
    }
    if (error instanceof AxiosError) {
      throw new Error(
        String(error.response?.data ?? "") || "Failed to search manufacturers"
      );
    }
    throw error;
  }
};

export const updateCustomer = async (customer: Customer) => {
  const response: AxiosResponse<Customer> = await http.put(
    API_BASE_URL + "/customer/" + customer.id,
    customer
  );
  return response.data;
};

export const deleteCustomer = async (id: number): Promise<string> => {
  const response: AxiosResponse<string> = await http.delete(
    API_BASE_URL + "/customer/" + id
  );
  return response.data;
};

export const getCustomers = async () => {
  const response: AxiosResponse<Customer[]> = await http.get(
    API_BASE_URL + "/customer"
  );
  return response.data;
};

export const getCustomer = async (id: number) => {
  const response: AxiosResponse<Customer> = await http.get(
    API_BASE_URL + "/customer/" + id
  );
  return response.data;
};

export const VENDORS_API_URL = `${API_BASE_URL}/vendor`;

export const getVendors = async (): Promise<Vendor[]> => {
  try {
    const response = await http.get(VENDORS_API_URL);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to fetch vendors");
    }
    throw error;
  }
};

export const getVendor = async (id: number): Promise<Vendor> => {
  try {
    const response = await http.get(`${VENDORS_API_URL}/${id}`);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to fetch vendor");
    }
    throw error;
  }
};

export const createVendor = async (
  vendor: Omit<Vendor, "id" | "createdAt" | "updatedAt"> & {
    newContact?: {
      role: VendorContact["role"];
      name: string;
      email: string;
      phone: string;
    };
  }
): Promise<Vendor> => {
  try {
    const response = await http.post(VENDORS_API_URL, vendor);
    return response.data;
  } catch (error) {
    throw new Error(apiErrorMessage(error, "Failed to create vendor"));
  }
};

export const updateVendor = async (vendor: Vendor): Promise<Vendor> => {
  try {
    const response = await http.put(`${VENDORS_API_URL}/${vendor.id}`, vendor);
    return response.data;
  } catch (error) {
    throw new Error(apiErrorMessage(error, "Failed to update vendor"));
  }
};

export const deleteVendor = async (id: number): Promise<string> => {
  try {
    const response = await http.delete(`${VENDORS_API_URL}/${id}`);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to delete vendor");
    }
    throw error;
  }
};

export const shopGoogle = async (
  searchTerm: string
): Promise<GoogleShopSearchResults[]> => {
  try {
    const response = await http.post(`${API_BASE_URL}/search/google/shop`, {
      searchTerm,
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Unable to fetch Google Shop Search Results"
      );
    }
    throw error;
  }
};

export const getGoogleProductDetails = async (
  pageToken: string
): Promise<ProductResults> => {
  try {
    const response = await http.post(`${API_BASE_URL}/search/google/product`, {
      pageToken,
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Unable to fetch Google Product Details"
      );
    }
    throw error;
  }
};

export const MANUFACTURERS_API_URL = `${API_BASE_URL}/mfg`;

export const getManufacturers = async (): Promise<Manufacturer[]> => {
  try {
    const response = await http.get(MANUFACTURERS_API_URL);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to fetch manufacturers");
    }
    throw error;
  }
};

export const getManufacturer = async (id: number): Promise<Manufacturer> => {
  try {
    const response = await http.get(`${MANUFACTURERS_API_URL}/${id}`);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to fetch manufacturer");
    }
    throw error;
  }
};

export const createManufacturer = async (
  manufacturer: Omit<Manufacturer, "id" | "createdAt" | "updatedAt"> & {
    newContact?: {
      role: ManufacturerContact["role"];
      name: string;
      email: string;
      phone: string;
    };
  }
): Promise<Manufacturer> => {
  try {
    const response = await http.post(MANUFACTURERS_API_URL, manufacturer);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to create manufacturer");
    }
    throw error;
  }
};

export const updateManufacturer = async (
  manufacturer: Manufacturer
): Promise<Manufacturer> => {
  try {
    const response = await http.put(
      `${MANUFACTURERS_API_URL}/${manufacturer.id}`,
      manufacturer
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to update manufacturer");
    }
    throw error;
  }
};

export const RFQ_API_URL = `${API_BASE_URL}/rfq`;

export interface CreateQuoteRequest {
  customerId: number;
  referenceNum: string;
  buyerId: number;
  buyerName?: string | null;
  dueDate: string;
  assignedToId?: number;
  priority?: number;
}

export interface UpdateQuoteRequest {
  referenceNum?: string;
  assignedToId?: number | null;
  status?: RFQStatus;
  dueDate?: string;
  poDueDate?: string | null;
  note?: string | null;
  customerId?: number | null;
  buyerId?: number | null;
  buyerName?: string | null;
  poNumber?: string | null;
  orderConfirmation?: string | null;
  receivedBy?: string | null;
  receivedDate?: string | null;
  approvedBy?: string | null;
  approvedDate?: string | null;
  quotedBy?: string | null;
  priority?: number;
  creditCardRatePercent?: number;
}

export interface UpdateItemRequest {
  name?: string;
  description?: string;
  partNumber?: string;
  quantity?: number;
  notes?: string;
  vendorRequestType?: VendorRequestType;
}

export interface RfqSearchResult {
  id: number;
  referenceNum: string;
  status: RFQStatus;
  customerName: string | null;
  /** RFQ header, line, and inbound PO numbers that matched (or the header PO). */
  poNumbers: string[];
  createdAt: string;
}

/** Find RFQs by RFQ # or PO # (header, line, or inbound PO), across all dates. */
export const searchRfqs = async (
  q: string,
  options?: { limit?: number; signal?: AbortSignal }
): Promise<RfqSearchResult[]> => {
  try {
    const response = await http.get<RfqSearchResult[]>(
      `${RFQ_API_URL}/search`,
      {
        params: { q, limit: options?.limit ?? 10 },
        signal: options?.signal,
      }
    );
    return response.data;
  } catch (error) {
    if ((error as { code?: string }).code === "ERR_CANCELED") {
      throw error;
    }
    if (error instanceof AxiosError) {
      throw new Error(
        String(error.response?.data ?? "") || "Failed to search RFQs"
      );
    }
    throw error;
  }
};

export const getQuotes = async (): Promise<Rfq[]> => {
  try {
    const response = await http.get(RFQ_API_URL);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to fetch quotes");
    }
    throw error;
  }
};

export const getQuote = async (
  id: number,
  options?: { includeHistory?: boolean; signal?: AbortSignal }
): Promise<Rfq> => {
  try {
    const response = await http.get(`${RFQ_API_URL}/${id}`, {
      params: options?.includeHistory ? { includeHistory: true } : undefined,
      signal: options?.signal,
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to fetch quote");
    }
    throw error;
  }
};

/** Shopping tab state + AI candidates (lazy; not included in getQuote). */
export interface AdminFeeReportRow {
  rfqId: number;
  homeworkId: number;
  referenceNum: string;
  customerName: string | null;
  poNumber: string | null;
  poReceivedDate: string | null;
  quoteSentAt: string | null;
  creditCardRatePercent: number;
  vendorQuotes: VendorQuote[];
}

export const getAdminFeeReport = async (): Promise<AdminFeeReportRow[]> => {
  try {
    const response = await http.get(`${RFQ_API_URL}/admin-fee-report`);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to load admin fee report");
    }
    throw error;
  }
};

export interface RfqTimingInterval {
  id: number;
  status: string;
  assignedToId: number | null;
  startedAt: string;
  endedAt: string | null;
  durationSeconds: number;
}

export interface RfqTiming {
  intervals: RfqTimingInterval[];
  statusTotals: { status: string; assignedToId: number | null; seconds: number }[];
  activeTotals: { userId: number; status: string; seconds: number }[];
  trackingSince: string | null;
  serverNow: string;
  /** False when totals are limited to the current user. */
  isAdminView: boolean;
}

export const getRfqTiming = async (rfqId: number): Promise<RfqTiming> => {
  try {
    const response = await http.get(`${RFQ_API_URL}/${rfqId}/timing`);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to load RFQ timing");
    }
    throw error;
  }
};

export const getRfqStatusSince = async (
  rfqId: number
): Promise<{ status: string | null; since: string | null }> => {
  try {
    const response = await http.get(`${RFQ_API_URL}/${rfqId}/status-since`);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to load status time");
    }
    throw error;
  }
};

export interface RfqTimingReportRow {
  userId: number;
  rfqId: number;
  status: string;
  seconds: number;
}

export interface RfqTimingReport {
  statusRows: RfqTimingReportRow[];
  activeRows: (RfqTimingReportRow & { lastActiveAt: string | null })[];
  rfqs: { id: number; referenceNum: string; customerName: string | null }[];
  trackingSince: string | null;
}

export const getRfqTimingReport = async (
  from: Date | null,
  to: Date | null,
  userId?: number
): Promise<RfqTimingReport> => {
  try {
    const response = await http.get(`${RFQ_API_URL}/timing-report`, {
      params: {
        ...(from ? { from: from.toISOString() } : {}),
        ...(to ? { to: to.toISOString() } : {}),
        ...(userId ? { userId } : {}),
      },
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to load RFQ timing report");
    }
    throw error;
  }
};

export const postRfqActiveTime = async (
  rfqId: number,
  sessionId: number | null,
  activeSecondsDelta: number
): Promise<{ sessionId: number }> => {
  try {
    const response = await http.post(`${RFQ_API_URL}/${rfqId}/active-time`, {
      sessionId,
      activeSecondsDelta,
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to record active time");
    }
    throw error;
  }
};

export const getRfqShopping = async (
  rfqId: number
): Promise<{
  quotes: Array<{
    id: number;
    shoppingTabState: import("../models/HomeworkShoppingTabState").HomeworkShoppingTabState | null;
    items: Array<{
      id: number;
      shoppingCandidates: RfqItemShoppingCandidate[];
    }>;
  }>;
}> => {
  try {
    const response = await http.get(`${RFQ_API_URL}/${rfqId}/shopping`);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to get RFQ shopping data"
      );
    }
    throw error;
  }
};

const ZENKIT_API_URL = `${API_BASE_URL}/zenkit`;

export const getZenkitItems = async (options?: {
  rfqIds?: number[];
  queueIds?: number[];
  archived?: boolean;
}): Promise<ZenkitItem[]> => {
  try {
    const response = await http.get(ZENKIT_API_URL, {
      params: {
        ...(options?.rfqIds?.length
          ? { rfqIds: options.rfqIds.join(",") }
          : {}),
        ...(options?.queueIds?.length
          ? { queueIds: options.queueIds.join(",") }
          : {}),
        ...(options?.archived ? { archived: "true" } : { archived: "false" }),
      },
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to fetch Payload items");
    }
    throw error;
  }
};

export const getZenkitQueues = async (options?: {
  rfqIds?: number[];
  queueIds?: number[];
  archived?: boolean;
  /** PO # / queue # / item # search (required by the board for archived). */
  q?: string;
}): Promise<ZenkitQueue[]> => {
  try {
    const response = await http.get(`${ZENKIT_API_URL}/queues`, {
      params: {
        ...(options?.rfqIds?.length
          ? { rfqIds: options.rfqIds.join(",") }
          : {}),
        ...(options?.queueIds?.length
          ? { queueIds: options.queueIds.join(",") }
          : {}),
        ...(options?.archived ? { archived: "true" } : { archived: "false" }),
        ...(options?.q?.trim() ? { q: options.q.trim() } : {}),
      },
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to fetch Payload queues");
    }
    throw error;
  }
};

export const countZenkitQueues = async (options: {
  archived: boolean;
  q?: string;
}): Promise<number> => {
  try {
    const response = await http.get(`${ZENKIT_API_URL}/queues/count`, {
      params: {
        archived: options.archived ? "true" : "false",
        ...(options.q?.trim() ? { q: options.q.trim() } : {}),
      },
      skipGlassPane: true,
    } as Parameters<typeof http.get>[1]);
    return Number(response.data?.count ?? 0);
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to count Payload queues");
    }
    throw error;
  }
};

export const getZenkitTagPriority = async (): Promise<string[]> => {
  try {
    const response = await http.get(`${ZENKIT_API_URL}/settings/tag-priority`, {
      skipGlassPane: true,
    } as Parameters<typeof http.get>[1]);
    return Array.isArray(response.data?.tags) ? response.data.tags : [];
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to load Payload tag priority",
      );
    }
    throw error;
  }
};

export const saveZenkitTagPriority = async (
  tags: string[]
): Promise<string[]> => {
  try {
    const response = await http.put(`${ZENKIT_API_URL}/settings/tag-priority`, {
      tags,
    });
    return Array.isArray(response.data?.tags) ? response.data.tags : tags;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to save Payload tag priority",
      );
    }
    throw error;
  }
};

export type ZenkitPoLines = {
  lines: Array<{
    rfqId: number;
    lineNumber: number;
    matchedBy: "line" | "header";
    item: RfqItem;
  }>;
  rfqs: Array<{
    id: number;
    referenceNum: string;
    poNumber: string | null;
    buyerName: string | null;
    customerName: string | null;
    customerId: number | null;
    orderConfirmation: string;
  }>;
};

/** RFQ lines (with vendor quotes) whose line or RFQ header PO # matches `po`. */
export const getZenkitPoLines = async (po: string): Promise<ZenkitPoLines> => {
  try {
    const response = await http.get(`${ZENKIT_API_URL}/po-lines`, {
      params: { po },
      skipGlassPane: true,
    } as Parameters<typeof http.get>[1]);
    return {
      lines: Array.isArray(response.data?.lines) ? response.data.lines : [],
      rfqs: Array.isArray(response.data?.rfqs) ? response.data.rfqs : [],
    };
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to look up RFQ by PO #");
    }
    throw error;
  }
};

export type ZenkitDefaultViewRecord = {
  id: string;
  name: string;
  config: Record<string, unknown>;
};

/** Admin-managed default views; `null` until an admin first saves them. */
export const getZenkitDefaultViews = async (): Promise<
  ZenkitDefaultViewRecord[] | null
> => {
  try {
    const response = await http.get(`${ZENKIT_API_URL}/settings/default-views`, {
      skipGlassPane: true,
    } as Parameters<typeof http.get>[1]);
    return Array.isArray(response.data?.views) ? response.data.views : null;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to load Payload default views",
      );
    }
    throw error;
  }
};

export const saveZenkitDefaultViews = async (
  views: ZenkitDefaultViewRecord[]
): Promise<ZenkitDefaultViewRecord[]> => {
  try {
    const response = await http.put(`${ZENKIT_API_URL}/settings/default-views`, {
      views,
    });
    return Array.isArray(response.data?.views) ? response.data.views : views;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to save Payload default views",
      );
    }
    throw error;
  }
};

export const reorderZenkitQueues = async (
  queueIds: number[]
): Promise<ZenkitQueue[]> => {
  try {
    const response = await http.post(`${ZENKIT_API_URL}/queues/reorder`, {
      queueIds,
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to reorder Payload queues"
      );
    }
    throw error;
  }
};

export const importRfqToZenkit = async (
  rfqId: number
): Promise<ZenkitQueue[]> => {
  try {
    const response = await http.post(`${ZENKIT_API_URL}/from-rfq/${rfqId}`);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to add RFQ items to Payload"
      );
    }
    throw error;
  }
};

export const createZenkitItem = async (
  item: ZenkitItemInput
): Promise<ZenkitItem> => {
  try {
    const response = await http.post(ZENKIT_API_URL, item);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to create Payload item");
    }
    throw error;
  }
};

export type ZenkitTrackLookupResult = {
  carrier: "fedex" | "ups" | "usps";
  trackingNumber: string;
  status: string | null;
  statusDescription: string | null;
  estimatedDelivery: string | null;
  hasException: boolean;
  exceptionSummary: string | null;
  unsupported?: boolean;
  credentialsMissing?: boolean;
};

export const lookupZenkitTracking = async (input: {
  trackingNumber: string;
  carrier: "fedex" | "ups" | "usps";
  service?: "ground" | "freight" | null;
}): Promise<ZenkitTrackLookupResult> => {
  try {
    const response = await http.post(
      `${ZENKIT_API_URL}/track-lookup`,
      input,
      { skipGlassPane: true } as Parameters<typeof http.post>[2],
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      // 503 returns structured payload when credentials are missing
      if (
        error.response?.status === 503 &&
        error.response.data &&
        typeof error.response.data === "object"
      ) {
        return error.response.data as ZenkitTrackLookupResult;
      }
      throw new Error(
        typeof error.response?.data === "string"
          ? error.response.data
          : "Failed to look up tracking",
      );
    }
    throw error;
  }
};

export const updateZenkitItem = async (
  id: number,
  item: Partial<ZenkitItemInput>
): Promise<ZenkitItem> => {
  try {
    const response = await http.put(`${ZENKIT_API_URL}/${id}`, item);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to update Payload item");
    }
    throw error;
  }
};

export const archiveZenkitItem = async (id: number): Promise<ZenkitItem> => {
  try {
    const response = await http.post(`${ZENKIT_API_URL}/${id}/archive`);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to archive Payload item");
    }
    throw error;
  }
};

export const unarchiveZenkitItem = async (id: number): Promise<ZenkitItem> => {
  try {
    const response = await http.post(`${ZENKIT_API_URL}/${id}/unarchive`);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to restore Payload item");
    }
    throw error;
  }
};

export const archiveZenkitRfqs = async (
  rfqIds: number[]
): Promise<{ updated: number }> => {
  try {
    const response = await http.post(`${ZENKIT_API_URL}/archive-rfqs`, {
      rfqIds,
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to archive Payload RFQs");
    }
    throw error;
  }
};

export const archiveZenkitQueues = async (
  queueIds: number[]
): Promise<{ updated: number }> => {
  try {
    const response = await http.post(`${ZENKIT_API_URL}/archive-queues`, {
      queueIds,
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to archive Payload queues"
      );
    }
    throw error;
  }
};

export type ZenkitNote = {
  id: number;
  zenkitQueueId: number;
  /** `tracking` notes show under the tracking numbers, not in Comments. */
  kind?: "tracking";
  body: string;
  createdByUserId: number | null;
  createdByName: string;
  createdAt: string;
  deletedAt?: string;
  deletedByName?: string;
};

export type ZenkitAttachmentMeta = {
  id: number;
  zenkitQueueId: number;
  fileName: string;
  contentType: string;
  contentBase64?: string;
  uploadedByUserId: number | null;
  uploadedByName: string;
  createdAt: string;
};

export const searchZenkitQueues = async (q: string): Promise<ZenkitQueue[]> => {
  try {
    const response = await http.get(`${ZENKIT_API_URL}/search`, {
      params: { q },
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to search Payload");
    }
    throw error;
  }
};

export const duplicateZenkitQueue = async (
  queueId: number
): Promise<ZenkitQueue> => {
  try {
    const response = await http.post(
      `${ZENKIT_API_URL}/queues/${queueId}/duplicate`
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to duplicate Payload");
    }
    throw error;
  }
};

export const unarchiveZenkitQueue = async (
  queueId: number
): Promise<ZenkitQueue> => {
  try {
    const response = await http.post(
      `${ZENKIT_API_URL}/queues/${queueId}/unarchive`
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to reinstate Payload");
    }
    throw error;
  }
};

export const getZenkitNotes = async (
  queueId: number
): Promise<ZenkitNote[]> => {
  try {
    const response = await http.get(
      `${ZENKIT_API_URL}/queues/${queueId}/notes`
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to load Payload notes");
    }
    throw error;
  }
};

export const createZenkitNote = async (
  queueId: number,
  body: string,
  kind?: ZenkitNote["kind"]
): Promise<ZenkitNote> => {
  try {
    const response = await http.post(
      `${ZENKIT_API_URL}/queues/${queueId}/notes`,
      { body, ...(kind ? { kind } : {}) }
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to create Payload note");
    }
    throw error;
  }
};

/** Soft delete; the comment can be brought back with `restoreZenkitNote`. */
export const deleteZenkitNote = async (
  queueId: number,
  noteId: number
): Promise<ZenkitNote> => {
  try {
    const response = await http.delete(
      `${ZENKIT_API_URL}/queues/${queueId}/notes/${noteId}`
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to delete comment");
    }
    throw error;
  }
};

export const restoreZenkitNote = async (
  queueId: number,
  noteId: number
): Promise<ZenkitNote> => {
  try {
    const response = await http.post(
      `${ZENKIT_API_URL}/queues/${queueId}/notes/${noteId}/restore`
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to restore comment");
    }
    throw error;
  }
};

export const getZenkitAttachments = async (
  queueId: number
): Promise<ZenkitAttachmentMeta[]> => {
  try {
    const response = await http.get(
      `${ZENKIT_API_URL}/queues/${queueId}/attachments`
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to load Payload attachments"
      );
    }
    throw error;
  }
};

export const uploadZenkitAttachment = async (
  queueId: number,
  payload: { fileName: string; contentType: string; contentBase64: string }
): Promise<ZenkitAttachmentMeta> => {
  try {
    const response = await http.post(
      `${ZENKIT_API_URL}/queues/${queueId}/attachments`,
      payload
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to upload Payload attachment"
      );
    }
    throw error;
  }
};

export const getZenkitAttachmentContent = async (
  attachmentId: number
): Promise<ZenkitAttachmentMeta> => {
  try {
    const response = await http.get(
      `${ZENKIT_API_URL}/attachments/${attachmentId}`
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to load Payload attachment"
      );
    }
    throw error;
  }
};

export const deleteZenkitAttachment = async (
  attachmentId: number
): Promise<{ deleted: boolean }> => {
  try {
    const response = await http.delete(
      `${ZENKIT_API_URL}/attachments/${attachmentId}`
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to delete Payload attachment"
      );
    }
    throw error;
  }
};

export const getZenkitCallOverdue = async (): Promise<{
  queues: ZenkitQueue[];
}> => {
  try {
    const response = await http.get(`${ZENKIT_API_URL}/call-overdue`);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to load overdue call dates"
      );
    }
    throw error;
  }
};

export const notifyZenkitSalesTeams = async (payload: {
  poNumber?: string;
  clientName?: string;
  vendorName?: string;
  description: string;
  mentionTargets?: string[];
  reportedBy?: string;
}): Promise<{ sent: boolean; skippedReason?: string; message: string }> => {
  try {
    const response = await http.post(
      `${ZENKIT_API_URL}/notify-sales`,
      payload,
      { skipGlassPane: true } as Parameters<typeof http.post>[2]
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to notify Sales on Teams"
      );
    }
    throw error;
  }
};

export type ZenkitSharedTab = {
  id: number;
  name: string;
  color: string;
  config: Record<string, unknown>;
  createdByUserId: number | null;
  createdAt: string;
  updatedAt: string;
};

export const getZenkitSharedTabs = async (): Promise<ZenkitSharedTab[]> => {
  try {
    const response = await http.get(`${ZENKIT_API_URL}/shared-tabs`);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to load shared Payload tabs");
    }
    throw error;
  }
};

export const createZenkitSharedTab = async (payload: {
  name: string;
  color?: string;
  config?: Record<string, unknown>;
}): Promise<ZenkitSharedTab> => {
  try {
    const response = await http.post(`${ZENKIT_API_URL}/shared-tabs`, payload);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to create shared Payload tab");
    }
    throw error;
  }
};

export const updateZenkitSharedTab = async (
  id: number,
  payload: {
    name?: string;
    color?: string;
    config?: Record<string, unknown>;
  }
): Promise<ZenkitSharedTab> => {
  try {
    const response = await http.put(
      `${ZENKIT_API_URL}/shared-tabs/${id}`,
      payload
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to update shared Payload tab");
    }
    throw error;
  }
};

export const deleteZenkitSharedTab = async (
  id: number
): Promise<{ deleted: boolean }> => {
  try {
    const response = await http.delete(`${ZENKIT_API_URL}/shared-tabs/${id}`);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to delete shared Payload tab");
    }
    throw error;
  }
};

export const exportRfqSpreadsheet = async (
  rfqId: number,
  homeworkId: number
): Promise<Blob> => {
  try {
    const response = await http.get(`${RFQ_API_URL}/${rfqId}/export/xlsm`, {
      params: { homeworkId },
      responseType: "blob",
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to export RFQ spreadsheet"
      );
    }
    throw error;
  }
};

const QB_SYNC_API_URL = `${API_BASE_URL}/qb-sync`;

/**
 * QuickBooks Estimate {@code SalesRepRef} / REP field (qbwc reads {@code rep} then {@code buyer}).
 * Must match a Sales Rep FullName in QuickBooks.
 */
export const QUICKBOOKS_ESTIMATE_REP_NAME = "Susan";

/** QuickBooks estimate/invoice TERMS for non-NASA customers. */
export const QUICKBOOKS_ESTIMATE_TERMS_DEFAULT = "Net 45";
/** QuickBooks estimate/invoice TERMS when the customer name is NASA. */
export const QUICKBOOKS_ESTIMATE_TERMS_NASA = "Credit Card";

/** NASA pays by credit card; every other customer (incl. NASA COMET) is Net 45. */
export function quickBooksEstimateTermsForCustomer(
  customerName?: string | null
): string {
  return customerPaysByCreditCard(customerName)
    ? QUICKBOOKS_ESTIMATE_TERMS_NASA
    : QUICKBOOKS_ESTIMATE_TERMS_DEFAULT;
}

/** Rocket City Supply ship-to / company address block for QB previews. */
export const QUICKBOOKS_RCS_ADDRESS_BLOCK =
  "Rocket City Supply\n1200 White Drive, Ste. A\nTitusville, FL 32780";

/** QuickBooks Desktop postal address (BillAddress / ShipAddress). */
export type QbPostalAddress = {
  addr1?: string;
  addr2?: string;
  addr3?: string;
  addr4?: string;
  addr5?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
};

export function formatQbPostalAddressBlock(
  addr: QbPostalAddress | null | undefined
): string {
  if (!addr || typeof addr !== "object") return "";
  const lines: string[] = [];
  for (const key of ["addr1", "addr2", "addr3", "addr4", "addr5"] as const) {
    const v = String(addr[key] ?? "").trim();
    if (v) lines.push(v);
  }
  const city = String(addr.city ?? "").trim();
  const state = String(addr.state ?? "").trim();
  const postal = String(addr.postalCode ?? "").trim();
  const cityLine = [city, [state, postal].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ");
  if (cityLine) lines.push(cityLine);
  const country = String(addr.country ?? "").trim();
  if (country) lines.push(country);
  return lines.join("\n");
}

/** Default ship-to: customer ShipAddress from QB, else RCS company address. */
export function quickBooksDefaultShipToBlock(
  shipAddress: QbPostalAddress | null | undefined
): string {
  return formatQbPostalAddressBlock(shipAddress) || QUICKBOOKS_RCS_ADDRESS_BLOCK;
}

/** QB CustomerSalesTaxCodeRef on estimates (must match qbwc `QB_ESTIMATE_CUSTOMER_SALES_TAX_CODE`). */
export const QUICKBOOKS_ESTIMATE_CUSTOMER_TAX_CODE = "Non";

export interface QbSyncQueueResponse {
  queued: number;
  ids: number[];
  /** PO #s whose estimate is already in QuickBooks with every sent line (nothing queued). */
  skippedEstimatePos?: string[];
  /** Legacy combined estimate was already queued or sent. */
  skippedLegacyEstimate?: boolean;
  /** Purchase orders actually queued. Vendor prefetch rows are not included. */
  purchaseOrdersQueued?: number;
  /** Vendors whose purchase order is already queued or in QuickBooks. */
  skippedPurchaseOrderVendors?: string[];
}

export interface QbSyncItemStatus {
  id: number;
  /** `reset`: was synced, then an admin unlocked it so its lines can be re-sent. */
  status: "pending" | "syncing" | "synced" | "error" | "reset";
  syncType?: string;
  itemName: string;
  /** Estimate rows only: customer PO this estimate belongs to; null = legacy combined estimate. */
  estimatePoKey?: string | null;
  /** Estimate rows only: lowercased item names of the product lines on this estimate. */
  lineItemNames?: string[] | null;
  /** Purchase order rows: QuickBooks vendor name. */
  vendor?: string | null;
  /** Purchase order rows: directory vendor id. */
  vendorId?: number | null;
  qbListId: string | null;
  errorMessage: string | null;
  createdAt: string;
  syncedAt: string | null;
}

export interface QbBillReadiness {
  ready: boolean;
  reason: string | null;
  purchaseOrderCount: number;
  syncedPurchaseOrderCount: number;
  pendingBillCount: number;
  syncedBillCount: number;
  errorBillCount: number;
}

export interface QbSyncStatusResponse {
  rfqReference: string;
  items: QbSyncItemStatus[];
  billReadiness: QbBillReadiness;
}

export interface QbQueueBillsResponse {
  rfqReference: string;
  queued: number;
  skipped: number;
  ids: number[];
  billReadiness: QbBillReadiness;
}

/** Input shape for building QuickBooks estimate lines (export grid / queue). */
export interface EstimateLineInput {
  itemName: string;
  salesDescription: string;
  purchaseDescription?: string;
  qty: number;
  salesPrice: number;
  cost?: number;
  unitOfMeasure?: string;
  modelSkuCatalog?: string;
  po?: string;
  mfgAndPhone?: string;
  mfgPartNumber?: string;
  /**
   * All client (homework) part #s for estimate/PO product Desc — one labeled
   * line each. When set, replaces the single mfgPartNumber Desc line.
   */
  clientPartNumbers?: Array<{ type: string; value: string }>;
  /**
   * Vendor part #s that differ from client part #s — shown on the estimate/PO
   * footer (not the product Desc line).
   */
  vendorPartNumber?: string;
  /** Starred part number type label for QB description (e.g. Catalog #, Sku #). */
  partNumberLabel?: string;
  /** RFQ item Trade Name — estimate description line only when set. */
  tradeName?: string;
  /** RFQ item Alt Mfg — estimate description line only when set. */
  altMfgName?: string;
  /**
   * QC codes → estimate/PO Desc "Quality Clauses:" only.
   * Not written to the Item "Special Note" custom field.
   */
  specialNote?: string;
  /**
   * Vendor special notes for the QB Item list "Special Note" custom field
   * (same content style as the client price quote special-note field).
   */
  itemSpecialNote?: string;
  /**
   * Vendor-quote special notes (external + internal) for the estimate footer block.
   * Separate from `specialNote` (QC → Quality Clauses) and `itemSpecialNote`.
   */
  footerSpecialNotes?: string;
  /** RFQ Source (preferredVendorName). Used for the Source: description line — not the selected vendor. */
  preferredVendor?: string;
  vendorAndPhone?: string;
  vendorEmail?: string;
  /** Selected vendor contact phone (falls back to vendor phone) — estimate footer Phone: row. */
  vendorPhone?: string;
  contactAndEmail?: string;
  orderConfirmation?: string;
  paymentTerms?: string;
  agent?: string;
  dueDate?: string;
  /** When true, append direct-delivery boilerplate to the estimate line description. */
  deliveredDirect?: boolean;
  /** Optional label shown above this row’s vendor block on the estimate footer (e.g. homework line number + name). */
  lineItemLabel?: string;
  /** Homework short display name — stripped from Desc when it restates the Item column. */
  shortDescription?: string;
  /** RFQ line number (displayOrder + 1) — maps to estimate line Other2 as "Line N". */
  rfqLineNumber?: number;
  /**
   * Itemized customer fees (Not Included) as separate estimate lines. Product
   * `salesPrice` must exclude these when this array is non-empty.
   */
  estimateFeeLines?: Array<{
    feeType: string;
    description: string;
    qty: number;
    /** Line sell total (fee each × qty, plus admin and credit card fees when CC is checked). */
    amount: number;
    /** Worksheet Tax box → QuickBooks SalesTaxCodeRef "Tax". */
    taxable?: boolean;
  }>;
  /** Worksheet Tax box on the item row → QuickBooks SalesTaxCodeRef "Tax". */
  taxable?: boolean;
}

/** One line in the `lines` array queued for EstimateAdd / EstimateMod (matches JSON sent to qbwc). */
export interface QuickBooksEstimateQueueLine {
  itemName?: string;
  description: string;
  qty?: number;
  rate?: number;
  /** Legacy estimate markup; new payloads send the exact selling Amount. */
  markupRate?: number;
  /** Exact line sell total used by the preview. */
  amount?: number;
  /** Line SalesTaxCodeRef "Tax" (QuickBooks computes the tax). */
  taxable?: boolean;
  other1?: string;
  other2?: string;
  /** Line PO — used for header row; not repeated in product `description`. */
  po?: string;
  vendorFooterLine?: boolean;
  /** Dashed separator row before the vendor footer block. */
  estimateFooterSpacerLine?: boolean;
  /** Blank row after the dashed footer separator (QuickBooks visual gap). */
  estimateFooterBlankSpacerLine?: boolean;
  /** Blank row between PO# header and product lines (QuickBooks visual gap). */
  estimateHeaderSpacerLine?: boolean;
  /** Blank row between consecutive product lines (QuickBooks visual gap). */
  productLineSpacerLine?: boolean;
  /** Itemized customer fee line under a product (shipping, etc.). */
  estimateFeeLine?: boolean;
  /** PO# line at the top of the estimate (description-only in QB). */
  vendorHeaderLine?: boolean;
  /** Credit Card terms "Item(s) were charged on Credit Card" row (and its spacer). */
  creditCardChargeLine?: boolean;
  qbEstimateFooterSource?: {
    vendorAndPhone?: string;
    contactAndEmail?: string;
    vendorPhone?: string;
    vendorEmail?: string;
    orderConfirmation?: string;
    mfgPartNumber?: string;
    /**
     * Vendor part #s that differ from client part #s — rendered in the footer.
     */
    vendorPartNumber?: string;
    /** Vendor quote MODEL/SKU/CATALOG — QB item custom field only (not estimate Desc). */
    modelSkuCatalog?: string;
    /** Shown above Vendor:/Contact: rows in the combined footer. */
    lineItemLabel?: string;
    /** Vendor-quote special notes (external + internal) for this line’s footer block. */
    footerSpecialNotes?: string;
  };
}

export interface BillLineInput {
  itemName: string;
  purchaseDescription: string;
  qty: number;
  salesPrice: number;
  cost: number;
  shippingFee?: number;
  po?: string;
  preferredVendor?: string;
  vendorAndPhone?: string;
  paymentTerms?: string;
}

export interface QuickBooksBillQueueLine {
  itemName: string;
  description: string;
  qty: number;
  cost: number;
}

export interface QuickBooksBillQueueTransaction {
  syncType: "Bill";
  vendor: string;
  rfqNumber: string;
  dueDate?: string;
  paymentTerms: string;
  terms: string;
  poNumber: string;
  lines: QuickBooksBillQueueLine[];
}

export interface PurchaseOrderFeeLineInput {
  feeType: string;
  description?: string;
  qty: number;
  cost: number;
}

/** Line + header context for QuickBooks PO queue / preview. */
export interface PurchaseOrderLineInput {
  itemName: string;
  purchaseDescription: string;
  salesDescription?: string;
  qty: number;
  cost: number;
  /** Itemized shipping fee (added to PO/Bill unit cost; excluded from QB item COST: custom field). */
  shippingFee?: number;
  /** Base vendor item total before fees; used to keep PO cost and fee lines separate. */
  purchaseOrderBaseCost?: number;
  /** Vendor fees emitted as separate QuickBooks PO lines. */
  purchaseOrderFees?: PurchaseOrderFeeLineInput[];
  unitOfMeasure?: string;
  shortDescription?: string;
  po?: string;
  mfgAndPhone?: string;
  mfgPartNumber?: string;
  /** Vendor part #s differing from client — estimate/PO footer. */
  vendorPartNumber?: string;
  partNumberLabel?: string;
  tradeName?: string;
  altMfgName?: string;
  specialNote?: string;
  /** Vendor special notes for QB Item "Special Note" custom field. */
  itemSpecialNote?: string;
  /** Vendor-quote special notes (external + internal) for estimate/PO footer. */
  footerSpecialNotes?: string;
  modelSkuCatalog?: string;
  preferredVendor?: string;
  vendorAndPhone?: string;
  paymentTerms?: string;
  orderConfirmation?: string;
  lineItemLabel?: string;
  rfqLineNumber?: number;
  deliveredDirect?: boolean;
  /** Contact name / email line — with vendorPhone maps to QB PO header Other1 (capped before API). */
  contactAndEmail?: string;
  vendorEmail?: string;
  vendorPhone?: string;
  /** Directory vendor id — keeps a second purchase order from being created after a rename. */
  vendorId?: number | null;
  /** Shipping method for this vendor — maps to QB PO header Ship Via (ShipMethodRef). */
  shipVia?: string;
  /**
   * All client (homework) part #s for estimate/PO product Desc.
   */
  clientPartNumbers?: Array<{ type: string; value: string }>;
}

/** One PO line in the JSON queued for PurchaseOrderAdd (matches qbwc). */
export interface QuickBooksPurchaseOrderQueueLine {
  itemName?: string;
  description: string;
  qty?: number;
  cost?: number;
  /** Exact line total; sent as QB Amount so QB derives Rate (cost each × qty can drift a cent). */
  amount?: number;
  other1?: string;
  other2?: string;
  vendorFooterLine?: boolean;
  estimateFooterSpacerLine?: boolean;
  estimateFooterBlankSpacerLine?: boolean;
  estimateHeaderSpacerLine?: boolean;
  productLineSpacerLine?: boolean;
  vendorHeaderLine?: boolean;
  vendorFeeLine?: boolean;
}

/** One purchase order transaction in the QB sync queue (one per vendor). */
export interface QuickBooksPurchaseOrderQueueTransaction {
  syncType: "PurchaseOrder";
  vendor: string;
  vendorId?: number;
  rfqNumber: string;
  paymentTerms: string;
  terms: string;
  poNumber: string;
  shipVia?: string;
  assignedTo?: string;
  buyer?: string;
  customerJob?: string;
  vendorContact?: string;
  vendorEmail?: string;
  lines: QuickBooksPurchaseOrderQueueLine[];
}

const normalizeOptionalText = (value: unknown): string => {
  if (typeof value === "string") return value.trim();
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const formatPurchaseOrderFeeDescription = (
  fee: PurchaseOrderFeeLineInput
): string => {
  const label = normalizeOptionalText(fee.feeType)
    .replace(/_/g, " ")
    .replace(/\s+/g, " ");
  const description = normalizeOptionalText(fee.description);
  if (description && description.toLowerCase() !== label.toLowerCase()) {
    return `${label}: ${description}`;
  }
  return description || label || "Fee";
};

const formatEstimateFeeDescription = (fee: {
  feeType: string;
  description: string;
}): string =>
  formatPurchaseOrderFeeDescription({
    feeType: fee.feeType,
    description: fee.description,
    qty: 1,
    cost: 0,
  });

/** Grouping label when PO/Bill lines have no vendor name (matches ExportDialog). */
export const QUICKBOOKS_UNKNOWN_VENDOR_LABEL = "Unknown Vendor";

/** PO/Bill grouping: selected quote vendor only (not RFQ Source / preferredVendor). */
export function quickBooksVendorGroupKey(item: {
  preferredVendor?: string;
  vendorAndPhone?: string;
}): string {
  return (
    normalizeOptionalText(item.vendorAndPhone) ||
    QUICKBOOKS_UNKNOWN_VENDOR_LABEL
  );
}

/** Matches `QB_CUSTOMER_REF_FULL_NAME_MAX_LEN` in qbwc `QbXmlBuilder` — Customer / Customer:Job FullName. */
const QB_CUSTOMER_JOB_FULL_NAME_MAX_LEN = 209;

/**
 * Normalizes export customer text for QuickBooks Customer or Customer:Job list FullName before queueing.
 * Collapses whitespace, normalizes spaces around colons in {@code parent : job} style names, and truncates.
 */
export const normalizeCustomerJobForQuickBooks = (value: unknown): string => {
  let s = normalizeOptionalText(value);
  if (!s) return "";
  s = s.replace(/\s+/g, " ");
  s = s.replace(/\s*:\s*/g, ":");
  if (s.length > QB_CUSTOMER_JOB_FULL_NAME_MAX_LEN) {
    s = s.slice(0, QB_CUSTOMER_JOB_FULL_NAME_MAX_LEN);
  }
  return s;
};

/** Customer whose program PO # gets JAC01- prefixed for estimate Other + estimate PONumber. */
export const AMENTUM_COMET_CUSTOMER_NAME = "Amentum (Comet)";

/** Test customer that uses the same JAC01 PO mapping as Amentum (Comet). */
export const JAC01_TEST_CUSTOMER_NAME = "dont_use1";

const AMENTUM_COMET_PO_PREFIX = "JAC01-";
/** Matches an already-prefixed Comet PO (prefix is case-insensitive). */
const AMENTUM_COMET_PO_PATTERN = /^JAC01-(.+)$/i;

/**
 * True only when the QB customer/job is exactly "Amentum (Comet)"
 * (after whitespace normalize — never fuzzy-matched to other Amentum jobs).
 */
export const isAmentumCometCustomer = (customerName: string): boolean =>
  normalizeCustomerJobForQuickBooks(customerName) === AMENTUM_COMET_CUSTOMER_NAME;

/** JAC01-xxxx special mapping — Amentum (Comet) and test customer dont_use1. */
export const usesJac01EstimateRefNumberRule = (customerName: string): boolean => {
  const normalized = normalizeCustomerJobForQuickBooks(customerName);
  return (
    normalized === AMENTUM_COMET_CUSTOMER_NAME ||
    normalized === JAC01_TEST_CUSTOMER_NAME
  );
};

export interface QuickBooksEstimatePoHeaderFields {
  /** QB PONumber — JAC01-{po} for Comet / dont_use1; otherwise the raw PO. */
  poNumber: string;
  /** QB RefNumber (Estimate Number) — program PO without JAC01- for Comet / dont_use1. */
  estimateRefNumber: string;
  /** QB Other header — JAC01-{po} for Comet / dont_use1; same as poNumber otherwise. */
  otherHeader: string;
}

/**
 * Maps RFQ PO # to QuickBooks estimate header fields.
 * Amentum (Comet) / dont_use1: program PO (e.g. 12345) → RefNumber = 12345,
 * PONumber and Other = JAC01-12345 (prefix added when missing).
 * All other customers: same value on RefNumber, PONumber, and Other.
 */
export const resolveQuickBooksEstimatePoHeaders = (
  customerName: string,
  rawPoNumber: string
): QuickBooksEstimatePoHeaderFields => {
  const poNumber = normalizeOptionalText(rawPoNumber);
  if (!poNumber) {
    return { poNumber: "", estimateRefNumber: "", otherHeader: "" };
  }
  if (!usesJac01EstimateRefNumberRule(customerName)) {
    return { poNumber, estimateRefNumber: poNumber, otherHeader: poNumber };
  }
  const match = poNumber.match(AMENTUM_COMET_PO_PATTERN);
  const programPo = match?.[1] ?? poNumber;
  const jac01Po = `${AMENTUM_COMET_PO_PREFIX}${programPo}`;
  return {
    poNumber: jac01Po,
    estimateRefNumber: programPo,
    otherHeader: jac01Po,
  };
};

/**
 * Vendor PO header RefNumber / P.O. NO. — program PO without JAC01-
 * (same idea as estimate RefNumber; JAC01 stays on estimate Other/PONumber only).
 */
export const resolveQuickBooksPurchaseOrderPoHeader = (
  customerName: string,
  rawPoNumber: string
): string => {
  const { estimateRefNumber, poNumber } = resolveQuickBooksEstimatePoHeaders(
    customerName,
    rawPoNumber
  );
  if (!usesJac01EstimateRefNumberRule(customerName)) {
    return poNumber;
  }
  return estimateRefNumber || poNumber;
};

/**
 * PO value shown on the estimate description header line (`PO#: …`).
 * For JAC01 customers this is the same JAC01-{po} as estimate Other.
 */
export const resolveEstimateDescriptionHeaderPo = (
  customerName: string | undefined,
  rawPoNumber: string
): string => {
  const po = normalizeOptionalText(rawPoNumber);
  if (!po) return "";
  if (!customerName || !usesJac01EstimateRefNumberRule(customerName)) {
    return po;
  }
  return (
    resolveQuickBooksEstimatePoHeaders(customerName, po).otherHeader || po
  );
};

/** QuickBooks Desktop PO header limits (PurchaseOrderAdd OSR — not the same as estimate line Other fields). */
const QB_PO_HEADER_OTHER1_MAX_LEN = 25;
const QB_PO_HEADER_OTHER2_MAX_LEN = 29;
const QB_PO_FOB_MAX_LEN = 13;

const truncateForQbPurchaseOrderHeader = (
  value: string,
  maxLen: number
): string => {
  const t = value.trim();
  if (t.length <= maxLen) return t;
  return t.slice(0, maxLen);
};

/**
 * Split combined export "Contact and Email" text into name vs email (same rules as ZenkitDialog).
 * e.g. "Jane Doe jane@vendor.com" → contact "Jane Doe", email "jane@vendor.com"
 */
const parseContactAndEmailParts = (
  value: string
): { contact: string; email: string } => {
  const trimmed = value.trim();
  if (!trimmed) return { contact: "", email: "" };
  const parts = trimmed.split(/\s+/);
  const emailPart = parts.find((p) => p.includes("@"));
  if (!emailPart) return { contact: trimmed, email: "" };
  const contactParts = parts.filter((p) => p !== emailPart);
  return { contact: contactParts.join(" "), email: emailPart };
};

/** QB PO header Other1 (custom template “Vendor Contact Name & Phone”); QB limit 25 chars. */
const buildPurchaseOrderHeaderOther1 = (
  vendorItems: PurchaseOrderLineInput[]
): string => {
  for (const item of vendorItems) {
    const { contact } = parseContactAndEmailParts(
      normalizeOptionalText(item.contactAndEmail)
    );
    const phone = normalizeOptionalText(item.vendorPhone);
    const parts = [contact, phone].filter((p) => p.length > 0);
    if (parts.length > 0) {
      return parts.join(" · ");
    }
  }
  return "";
};

/** QB PO header Other2 (custom template “Vendor Email”); QB limit 29 chars. */
const buildPurchaseOrderHeaderOther2Email = (
  vendorItems: PurchaseOrderLineInput[]
): string => {
  for (const item of vendorItems) {
    const direct = normalizeOptionalText(item.vendorEmail);
    if (direct) return direct;
  }
  for (const item of vendorItems) {
    const { email } = parseContactAndEmailParts(
      normalizeOptionalText(item.contactAndEmail)
    );
    if (email) return email;
  }
  return "";
};

/** Full vendor contact string for PO header Other1 (before QB 25-char truncation). */
export function getPurchaseOrderVendorContactForPreview(
  items: PurchaseOrderLineInput[]
): string {
  return buildPurchaseOrderHeaderOther1(items);
}

/** Full vendor email for PO header Other2 (before QB 29-char truncation). */
export function getPurchaseOrderVendorEmailForPreview(
  items: PurchaseOrderLineInput[]
): string {
  return buildPurchaseOrderHeaderOther2Email(items);
}

/**
 * PO preview VENDOR box: name + address (matches QuickBooks Desktop layout).
 * Preview-only — not written into qbXML.
 */
export function formatPurchaseOrderVendorAddressBlock(
  vendorName: string,
  vendorAddress?: string | null
): string {
  const name = vendorName.trim();
  const addr = String(vendorAddress ?? "")
    .trim()
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n");
  if (!name) return addr;
  if (!addr) return name;
  const addrLower = addr.toLowerCase();
  const nameLower = name.toLowerCase();
  if (addrLower === nameLower || addrLower.startsWith(`${nameLower}\n`)) {
    return addr;
  }
  return `${name}\n${addr}`;
}

/**
 * Builds the same purchase-order queue payload as {@link createPurchaseOrdersForQuickBooks} (one object per vendor).
 */
export function buildPurchaseOrderQueueTransactions(
  rfqReference: string,
  items: PurchaseOrderLineInput[],
  assignedToForFob?: string,
  customerJobForPurchaseOrder?: string,
  /** RFQ buyer display name — preferred source for QB PO FOB (falls back to Assigned To). */
  buyerNameForFob?: string
): QuickBooksPurchaseOrderQueueTransaction[] {
  if (items.length === 0) {
    return [];
  }

  const estimateLineNumberByItem = new Map<PurchaseOrderLineInput, number>();
  items.forEach((item, index) => {
    estimateLineNumberByItem.set(item, index + 1);
  });

  const groupedByVendor = new Map<
    string,
    { vendor: string; items: PurchaseOrderLineInput[] }
  >();
  for (const item of items) {
    const vendorName = quickBooksVendorGroupKey(item);
    const vendorId = item.vendorId;
    const key =
      typeof vendorId === "number" && vendorId > 0
        ? `id:${vendorId}`
        : `name:${vendorName.toLowerCase()}`;

    const existing = groupedByVendor.get(key);
    if (existing) {
      existing.items.push(item);
    } else {
      groupedByVendor.set(key, { vendor: vendorName, items: [item] });
    }
  }

  if (groupedByVendor.size === 0) {
    return [];
  }

  const buyerFirst = buyerFirstNameFromDisplayName(buyerNameForFob);
  const fobBuyerRaw =
    buyerFirst || normalizeOptionalText(assignedToForFob);
  const fobBuyer = fobBuyerRaw
    ? truncateForQbPurchaseOrderHeader(fobBuyerRaw, QB_PO_FOB_MAX_LEN)
    : "";
  const customerJobForQb = normalizeCustomerJobForQuickBooks(
    customerJobForPurchaseOrder
  );

  return Array.from(groupedByVendor.values()).map(({ vendor, items: vendorItems }) => {
    const poValues = vendorItems
      .map((item) => normalizeOptionalText(item.po))
      .filter((value) => value.length > 0);
    const uniquePoValues = poValues.filter(
      (value, index, arr) => arr.indexOf(value) === index
    );
    const poNumberRaw = uniquePoValues[0] || "";
    const poNumber = resolveQuickBooksPurchaseOrderPoHeader(
      customerJobForQb,
      poNumberRaw
    );
    const paymentTerms =
      vendorItems
        .map((item) => normalizeOptionalText(item.paymentTerms))
        .find((value) => value.length > 0) || "";
    const shipViaRaw =
      vendorItems
        .map((item) => normalizeOptionalText(item.shipVia ?? ""))
        .find((value) => value.length > 0) || "";
    const shipVia = shipViaRaw ? shippingMethodToQuickBooks(shipViaRaw) : "";

    const estimateStyleLines = buildEstimateLinesForQuickBooks(
      vendorItems.map((item): EstimateLineInput => {
        const fallbackSalesDescription = normalizeOptionalText(
          item.salesDescription
        );
        const fallbackPurchaseDescription = normalizeOptionalText(
          item.purchaseDescription
        );
        const lineNumber = estimateLineNumberByItem.get(item);
        const poCostTotal =
          item.purchaseOrderBaseCost ??
          getVendorPurchaseTotalForQuickBooks(
            item.cost ?? 0,
            item.shippingFee ?? 0
          );
        return {
          itemName: item.itemName,
          salesDescription:
            fallbackSalesDescription || fallbackPurchaseDescription,
          purchaseDescription:
            fallbackPurchaseDescription || fallbackSalesDescription,
          qty: item.qty,
          salesPrice: poCostTotal,
          cost: poCostTotal,
          unitOfMeasure: item.unitOfMeasure,
          po: item.po,
          mfgAndPhone: item.mfgAndPhone,
          mfgPartNumber: item.mfgPartNumber,
          vendorPartNumber: item.vendorPartNumber,
          partNumberLabel: item.partNumberLabel,
          clientPartNumbers: item.clientPartNumbers,
          tradeName: item.tradeName,
          altMfgName: item.altMfgName,
          specialNote: item.specialNote,
          itemSpecialNote: item.itemSpecialNote,
          footerSpecialNotes: item.footerSpecialNotes,
          preferredVendor: item.preferredVendor,
          vendorAndPhone: item.vendorAndPhone,
          vendorEmail: item.vendorEmail,
          vendorPhone: item.vendorPhone,
          contactAndEmail: item.contactAndEmail,
          orderConfirmation: item.orderConfirmation,
          paymentTerms: item.paymentTerms,
          deliveredDirect: item.deliveredDirect,
          modelSkuCatalog: item.modelSkuCatalog,
          lineItemLabel: item.lineItemLabel,
          shortDescription: item.shortDescription,
          rfqLineNumber: item.rfqLineNumber ?? lineNumber,
        };
      }),
      customerJobForQb
    );

    let vendorItemIndex = 0;
    const lines: QuickBooksPurchaseOrderQueueLine[] =
      estimateStyleLines.flatMap((line) => {
        const descOnly =
          Boolean(line.vendorHeaderLine) ||
          Boolean(line.vendorFooterLine) ||
          Boolean(line.estimateHeaderSpacerLine) ||
          Boolean(line.estimateFooterSpacerLine) ||
          Boolean(line.estimateFooterBlankSpacerLine) ||
          Boolean(line.productLineSpacerLine);

        if (descOnly) {
          return [
            {
              itemName: line.itemName,
              description: line.description,
              qty: line.qty,
              cost: undefined,
              other1: line.other1,
              other2: line.other2,
              vendorHeaderLine: line.vendorHeaderLine,
              vendorFooterLine: line.vendorFooterLine,
              estimateHeaderSpacerLine: line.estimateHeaderSpacerLine,
              estimateFooterSpacerLine: line.estimateFooterSpacerLine,
              estimateFooterBlankSpacerLine: line.estimateFooterBlankSpacerLine,
              productLineSpacerLine: line.productLineSpacerLine,
            },
          ];
        }

        // Estimate fee lines are not PO merchandise — skip for vendorItemIndex alignment.
        if (line.estimateFeeLine) {
          return [];
        }

        const sourceItem = vendorItems[vendorItemIndex++];
        const qty = Number(line.qty ?? sourceItem?.qty ?? 0);
        const hasFeeLines = (sourceItem?.purchaseOrderFees?.length ?? 0) > 0;
        // Merchandise only when fee lines are present — do not bake shipping
        // each into Cost (printed PO was showing cost-each + shipping-each).
        const merchandiseTotal =
          sourceItem?.purchaseOrderBaseCost ??
          getVendorPurchaseTotalForQuickBooks(
            sourceItem?.cost ?? 0,
            hasFeeLines ? 0 : (sourceItem?.shippingFee ?? 0)
          );
        const unitCost = qbLineTotalPerUnit(merchandiseTotal, qty);

        const queueLine: QuickBooksPurchaseOrderQueueLine = {
          itemName: line.itemName,
          description: line.description,
          qty,
          cost: unitCost,
          amount: qbEstimateLineAmount(merchandiseTotal),
          other1: line.other1,
          other2: line.other2,
          vendorHeaderLine: line.vendorHeaderLine,
          vendorFooterLine: line.vendorFooterLine,
          estimateHeaderSpacerLine: line.estimateHeaderSpacerLine,
          estimateFooterSpacerLine: line.estimateFooterSpacerLine,
          estimateFooterBlankSpacerLine: line.estimateFooterBlankSpacerLine,
          productLineSpacerLine: line.productLineSpacerLine,
        };

        const feeLines: QuickBooksPurchaseOrderQueueLine[] = (
          sourceItem?.purchaseOrderFees ?? []
        ).map(
          (fee): QuickBooksPurchaseOrderQueueLine => ({
            itemName: line.itemName,
            description: formatPurchaseOrderFeeDescription(fee),
            qty: fee.qty,
            cost: fee.cost,
            vendorFeeLine: true,
          })
        );
        // If fees weren't materialized but shipping total exists, emit it as its
        // own PO line (qty 1 @ total) so it is not lost and not rolled into Rate.
        if (
          feeLines.length === 0 &&
          sourceItem?.shippingFee != null &&
          Number(sourceItem.shippingFee) !== 0 &&
          sourceItem.purchaseOrderBaseCost != null
        ) {
          feeLines.push({
            itemName: line.itemName,
            description: "SHIPPING",
            qty: 1,
            cost: Number(sourceItem.shippingFee),
            vendorFeeLine: true,
          });
        }
        return [queueLine, ...feeLines];
      });

    const vendorContactRaw = buildPurchaseOrderHeaderOther1(vendorItems);
    const vendorContact = vendorContactRaw
      ? truncateForQbPurchaseOrderHeader(
          vendorContactRaw,
          QB_PO_HEADER_OTHER1_MAX_LEN
        )
      : "";
    const vendorEmailRaw = buildPurchaseOrderHeaderOther2Email(vendorItems);
    const vendorEmailForHeader = vendorEmailRaw
      ? truncateForQbPurchaseOrderHeader(
          vendorEmailRaw,
          QB_PO_HEADER_OTHER2_MAX_LEN
        )
      : "";

    const vendorId = vendorItems
      .map((item) => item.vendorId)
      .find((id): id is number => typeof id === "number" && id > 0);

    return {
      syncType: "PurchaseOrder" as const,
      vendor,
      ...(vendorId != null ? { vendorId } : {}),
      rfqNumber: quickBooksRfqDisplayNumber(rfqReference),
      paymentTerms,
      terms: paymentTerms,
      poNumber,
      ...(shipVia ? { shipVia } : {}),
      ...(fobBuyer ? { fob: fobBuyer } : {}),
      ...(fobBuyer ? { assignedTo: fobBuyer } : {}),
      ...(fobBuyer ? { buyer: fobBuyer } : {}),
      ...(customerJobForQb ? { customerJob: customerJobForQb } : {}),
      ...(vendorContact ? { vendorContact } : {}),
      ...(vendorEmailForHeader ? { vendorEmail: vendorEmailForHeader } : {}),
      lines,
    };
  });
}

const splitDescriptionLines = (value: string): string[] => {
  const normalized = (value || "").replace(/\r\n/g, "\n");
  const lines = normalized
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  return lines.length > 0 ? lines : [""];
};

/** yyyy-MM-dd in local time for QB estimate TxnDate (header date when queued). */
const formatLocalDateYyyyMmDd = (d: Date = new Date()): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const formatFobDate = (dueDate: string | undefined): string => {
  if (!dueDate) return "";
  const raw = dueDate.trim();
  if (!raw) return "";

  // Calendar date only — avoid `new Date("yyyy-mm-dd")` / ISO-Z (UTC midnight shifts local day in US TZ).
  const datePart = raw.split("T")[0].trim();
  let cal = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(datePart);
  if (cal) {
    const month = parseInt(cal[2], 10);
    const day = parseInt(cal[3], 10);
    if (!Number.isNaN(month) && !Number.isNaN(day)) {
      return `${month}/${day}`;
    }
  }
  cal = /^(\d{4})\/(\d{1,2})\/(\d{1,2})$/.exec(datePart);
  if (cal) {
    const month = parseInt(cal[2], 10);
    const day = parseInt(cal[3], 10);
    if (!Number.isNaN(month) && !Number.isNaN(day)) {
      return `${month}/${day}`;
    }
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return normalizeOptionalText(raw);
  }
  const month = parsed.getMonth() + 1;
  const day = parsed.getDate();
  return `${month}/${day}`;
};

/** M/D tail for estimate FOB (matches qbwc / QuickBooks REP layout). */
export function formatEstimateFobDayLabel(dueDate: string | undefined): string {
  return formatFobDate(dueDate);
}

/** First token of buyer display name for QuickBooks estimate FOB header. */
export function buyerFirstNameFromDisplayName(
  name: string | undefined | null
): string {
  return (name ?? "").trim().split(/\s+/)[0] ?? "";
}

/** Legacy queue marker for footer rows (stripped before QB; prefer `vendorFooterLine`). */
export const QB_ESTIMATE_FOOTER_DESC_MARKER = "__QB_VF__";
/** Legacy queue marker for header rows (stripped before QB; prefer `vendorHeaderLine`). */
export const QB_ESTIMATE_HEADER_DESC_MARKER = "__QB_VH__";

/** Header row text (PO only). */
export const formatEstimateHeaderPoDescription = (po: string): string =>
  `PO#:  ${normalizeOptionalText(po)}`;

/** Footer labels share one value column across groups (Vendor / Contact / Phone / Email / Order / Note). */
const FOOTER_LABEL_MIN_WIDTH = "Contact";

/** Arial advance widths (1/1000 em); QuickBooks renders Desc in a proportional font. */
const QB_DESC_GLYPH_WIDTHS: Record<string, number> = {
  " ": 278, "\u00A0": 278, "#": 556, ":": 278, "-": 333, ".": 278, "/": 278,
  "&": 667, "(": 333, ")": 333, "'": 191,
  A: 667, B: 667, C: 722, D: 722, E: 667, F: 611, G: 778, H: 722, I: 278,
  J: 500, K: 667, L: 556, M: 833, N: 722, O: 778, P: 667, Q: 778, R: 722,
  S: 667, T: 611, U: 722, V: 667, W: 944, X: 667, Y: 667, Z: 611,
  a: 556, b: 556, c: 500, d: 556, e: 556, f: 278, g: 556, h: 556, i: 222,
  j: 222, k: 500, l: 222, m: 833, n: 556, o: 556, p: 556, q: 556, r: 333,
  s: 500, t: 278, u: 556, v: 500, w: 722, x: 500, y: 500, z: 500,
};
const QB_DESC_PAD = "\u00A0";
const QB_DESC_PAD_WIDTH = 278;

const measureQbDescText = (text: string): number =>
  Array.from(text).reduce(
    (sum, ch) => sum + (QB_DESC_GLYPH_WIDTHS[ch] ?? 556),
    0
  );

/** Left-aligned `Label:` padded with NBSP so the following value starts at `columnWidth`. */
const padQbDescLabel = (label: string, columnWidth: number): string => {
  const text = `${label}:`;
  const gap = Math.max(columnWidth - measureQbDescText(text), QB_DESC_PAD_WIDTH);
  return text + QB_DESC_PAD.repeat(Math.max(1, Math.round(gap / QB_DESC_PAD_WIDTH)));
};

/** Value column position: widest `Label:` (or `minLabel:`) plus one space. */
const qbDescValueColumn = (labels: string[], minLabel: string): number =>
  Math.max(...[minLabel, ...labels].map((l) => measureQbDescText(`${l}:`))) +
  QB_DESC_PAD_WIDTH;

/**
 * Align colon-labeled rows: labels flush left, values share one column.
 * Pads with NBSP so QuickBooks Desc (which collapses ordinary spaces) keeps the
 * value column. Backend encodes NBSP as &#160; in qbXML so the stream stays ASCII.
 */
function formatAlignedColonLines(
  entries: Array<{ label: string; value: string }>,
  minLabel: string
): string[] {
  const rows = entries
    .map((entry) => ({
      label: normalizeOptionalText(entry.label).replace(/:$/, ""),
      value: normalizeOptionalText(entry.value),
    }))
    .filter((row) => row.label.length > 0 && row.value.length > 0);
  if (rows.length === 0) return [];
  const column = qbDescValueColumn(
    rows.map((row) => row.label),
    minLabel
  );
  return rows.map((row) => `${padQbDescLabel(row.label, column)}${row.value}`);
}

const stripEstimateFooterDescMarker = (desc: string): string =>
  desc.replace(/\r?\n__QB_VF__\s*$/i, "").trimEnd();

const stripEstimateHeaderDescMarker = (desc: string): string =>
  desc.replace(/\r?\n__QB_VH__\s*$/i, "").trimEnd();

const stripEstimateVendorBlockDescMarkers = (desc: string): string =>
  stripEstimateFooterDescMarker(stripEstimateHeaderDescMarker(desc));

/** Invisible Desc for blank estimate spacer rows (QuickBooks requires non-empty Desc). */
export const QB_ESTIMATE_HEADER_SPACER_DESC = " ";

const buildEstimateHeaderSpacerLine = (): QuickBooksEstimateQueueLine => ({
  itemName: "",
  description: QB_ESTIMATE_HEADER_SPACER_DESC,
  estimateHeaderSpacerLine: true,
});

const buildEstimateFooterBlankSpacerLine = (): QuickBooksEstimateQueueLine => ({
  itemName: "",
  description: QB_ESTIMATE_HEADER_SPACER_DESC,
  estimateFooterBlankSpacerLine: true,
});

const buildProductLineSpacerLine = (): QuickBooksEstimateQueueLine => ({
  itemName: "",
  description: QB_ESTIMATE_HEADER_SPACER_DESC,
  productLineSpacerLine: true,
});

/** Inserts a blank desc-only row between each product line for QuickBooks layout. */
const interleaveProductLineSpacers = (
  lines: QuickBooksEstimateQueueLine[]
): QuickBooksEstimateQueueLine[] => {
  if (lines.length <= 1) return lines;
  const out: QuickBooksEstimateQueueLine[] = [];
  for (let i = 0; i < lines.length; i++) {
    out.push(lines[i]);
    if (i < lines.length - 1) {
      out.push(buildProductLineSpacerLine());
    }
  }
  return out;
};

const extractPoFromProductDescription = (desc: string): string => {
  const firstLine = desc.split(/\r?\n/)[0] ?? "";
  const m = firstLine.match(/^PO#:\s*(.*)$/i);
  return m ? m[1].trim() : "";
};

const resolveEstimatePoFromItems = (items: EstimateLineInput[]): string => {
  for (const item of items) {
    const po = normalizeOptionalText(item.po);
    if (po) return po;
  }
  return "";
};

const resolveEstimatePoFromProductLines = <
  T extends EstimateVendorBlockLine & { po?: string },
>(
  products: T[]
): string => {
  for (const p of products) {
    const fromField = normalizeOptionalText(p.po);
    if (fromField) return fromField;
    const fromDesc = extractPoFromProductDescription(p.description ?? "");
    if (fromDesc) return fromDesc;
  }
  return "";
};

const buildEstimateHeaderLineDescription = (
  po: string,
  customerName?: string
): string =>
  formatEstimateHeaderPoDescription(
    resolveEstimateDescriptionHeaderPo(customerName, po)
  );

const buildEstimateFooterLineDescription = (
  vendorBlockDescription: string
): string => stripEstimateVendorBlockDescMarkers(vendorBlockDescription).trim();

type EstimateVendorBlockLine = {
  vendorFooterLine?: boolean;
  vendorHeaderLine?: boolean;
  estimateFooterSpacerLine?: boolean;
  estimateFooterBlankSpacerLine?: boolean;
  estimateHeaderSpacerLine?: boolean;
  productLineSpacerLine?: boolean;
  description?: string;
  itemName?: string;
  qty?: number;
  rate?: number;
  other1?: string;
  other2?: string;
};

/** Collapses multiple vendor header/footer lines into one of each. */
function collapseEstimateVendorBlockLines<T extends EstimateVendorBlockLine>(
  lines: T[],
  customerName?: string
): T[] {
  const products: T[] = [];
  const headers: T[] = [];
  const footers: T[] = [];
  const headerSpacers: T[] = [];
  const footerBlankSpacers: T[] = [];
  for (const line of lines) {
    if (line.vendorHeaderLine === true) headers.push(line);
    else if (line.vendorFooterLine === true) footers.push(line);
    else if (line.estimateHeaderSpacerLine === true) headerSpacers.push(line);
    else if (line.estimateFooterSpacerLine === true) continue;
    else if (line.estimateFooterBlankSpacerLine === true)
      footerBlankSpacers.push(line);
    else if (line.productLineSpacerLine === true) continue;
    else products.push(line);
  }
  if (headers.length <= 1 && footers.length <= 1) return lines;

  const anchor =
    [...headers, ...footers]
      .map((l) => normalizeOptionalText(l.itemName))
      .find(Boolean) ||
    products.map((p) => normalizeOptionalText(p.itemName)).find(Boolean) ||
    "";
  if (!anchor) return lines;

  let out = interleaveProductLineSpacers(
    products as QuickBooksEstimateQueueLine[]
  ) as T[];

  if (headers.length >= 1) {
    const po = resolveEstimatePoFromProductLines(out);
    const headerSpacerLine = (headerSpacers[0] ??
      buildEstimateHeaderSpacerLine()) as T;
    out = [
      {
        ...headers[headers.length - 1],
        itemName: "",
        description: buildEstimateHeaderLineDescription(po, customerName),
        vendorHeaderLine: true as const,
        other1: "",
        other2: "",
      } as T,
      headerSpacerLine,
      ...out,
    ];
  }

  const footerSpacerLines = (): T[] => [
    (footerBlankSpacers[0] ?? buildEstimateFooterBlankSpacerLine()) as T,
  ];

  if (footers.length > 1) {
    const blocks = footers
      .map((f) =>
        stripEstimateVendorBlockDescMarkers(f.description ?? "").trim()
      )
      .filter((b) => b.length > 0);
    if (blocks.length > 0) {
      const body = `\n\n${blocks.join("\n\n")}`;
      out = [
        ...out,
        ...footerSpacerLines(),
        {
          ...footers[footers.length - 1],
          itemName: anchor,
          description: buildEstimateFooterLineDescription(body),
          vendorFooterLine: true as const,
          other1: "",
          other2: "",
        } as T,
      ];
    }
  } else if (footers.length === 1) {
    out = [
      ...out,
      ...footerSpacerLines(),
      {
        ...footers[0],
        description: buildEstimateFooterLineDescription(
          footers[0].description ?? ""
        ),
      } as T,
    ];
  }

  return out;
}

/** Trailing vendor / contact block (matches the description-only footer line on the QB estimate). */
/** Order confirmation # is written in by hand when the vendor has not sent one yet. */
const ORDER_CONFIRMATION_FILL = "_______________";

const buildVendorContactFooterBlock = (
  item: EstimateLineInput,
  indexInBatch: number
): string => {
  const lineNum =
    item.rfqLineNumber != null && item.rfqLineNumber > 0
      ? item.rfqLineNumber
      : indexInBatch + 1;
  const label =
    normalizeOptionalText(item.lineItemLabel) ||
    `Line ${lineNum} - ${normalizeOptionalText(item.itemName) || "Item"}`;
  const rows: string[] = [label];
  const colonEntries: Array<{ label: string; value: string }> = [];
  const v = normalizeOptionalText(item.vendorAndPhone);
  const c = normalizeOptionalText(item.contactAndEmail);
  const p = normalizeOptionalText(item.vendorPhone);
  const e = normalizeOptionalText(item.vendorEmail);
  const o = normalizeOptionalText(item.orderConfirmation);
  const notes = normalizeOptionalText(item.footerSpecialNotes);
  const vendorPart = normalizeOptionalText(item.vendorPartNumber);

  if (v) colonEntries.push({ label: "Vendor", value: v });
  if (vendorPart) {
    for (const line of vendorPart.split(/\r?\n/)) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      const colon = trimmed.indexOf(":");
      if (colon > 0) {
        colonEntries.push({
          label: trimmed.slice(0, colon).trim(),
          value: trimmed.slice(colon + 1).trim(),
        });
      } else {
        // Flush any pending aligned rows before a free-form line.
        rows.push(...formatAlignedColonLines(colonEntries, FOOTER_LABEL_MIN_WIDTH));
        colonEntries.length = 0;
        rows.push(trimmed);
      }
    }
  }
  if (c) colonEntries.push({ label: "Contact", value: c });
  if (p) colonEntries.push({ label: "Phone", value: p });
  if (e) colonEntries.push({ label: "Email", value: e });
  colonEntries.push({
    label: "Order Conf #",
    value: o.replace(/\s+/g, " ") || ORDER_CONFIRMATION_FILL,
  });
  rows.push(...formatAlignedColonLines(colonEntries, FOOTER_LABEL_MIN_WIDTH));

  if (notes) {
    const noteLines = notes.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    const column = qbDescValueColumn(["Note"], FOOTER_LABEL_MIN_WIDTH);
    const indent = QB_DESC_PAD.repeat(Math.round(column / QB_DESC_PAD_WIDTH));
    noteLines.forEach((line, i) => {
      rows.push(i === 0 ? `${padQbDescLabel("Note", column)}${line}` : `${indent}${line}`);
    });
  }

  return rows.join("\n");
};

/** Capitalized starred part # type for QuickBooks description lines (e.g. Catalog #, Sku #). */
export function formatQuickBooksPartNumberLabel(type: string): string {
  const t = normalizeOptionalText(type);
  if (!t) return "Part #";
  const lower = t.toLowerCase();
  return `${lower.charAt(0).toUpperCase()}${lower.slice(1)} #`;
}

/** Product Desc Mfg/Source/Part # value column sizes to the widest label present. */
const QB_DESC_LABEL_MIN_WIDTH = "";

/** QB estimate description lines for vendor direct-to-site delivery (fill-in fields). */
const DIRECT_DELIVERY_FILL = "_______________________________";

const buildDirectDeliveryBlock = (): string =>
  formatAlignedColonLines(
    [
      { label: "Date Delivered", value: DIRECT_DELIVERY_FILL },
      { label: "Tracking #", value: DIRECT_DELIVERY_FILL },
      { label: "Signed for by", value: DIRECT_DELIVERY_FILL },
    ],
    QB_DESC_LABEL_MIN_WIDTH
  ).join("\n");

/**
 * Product line Desc for QuickBooks estimates/POs — and the SalesDesc/PurchaseDesc
 * written to the QuickBooks item list on ItemAdd (same text).
 */
export const buildEstimateLineDescription = (
  item: EstimateLineInput
): string => {
  // Description body only — do not prepend QB item name or a DESC: label.
  // Also strip leading body lines that restate item/short name (common in seeds).
  // Strip Source/Mfg/Part # style lines so we can re-emit them with client-quote
  // colon alignment (same label order as the customer price quote letter).
  const descriptionLines = stripEstimateMetaColonLines(
    stripLeadingItemNameDescriptionLines(
      splitDescriptionLines(item.salesDescription).filter(
        (line) => line.length > 0
      ),
      item.itemName,
      item.shortDescription
    )
  );

  // Match customer price quote order: Mfg → Source → Trade Name → Alt Mfg → Part #s.
  const colonEntries: Array<{ label: string; value: string }> = [
    { label: "Mfg", value: item.mfgAndPhone || "" },
    { label: "Source", value: item.preferredVendor || "" },
    { label: "Trade Name", value: item.tradeName || "" },
    { label: "Alt Mfg", value: item.altMfgName || "" },
  ];
  const clientParts = item.clientPartNumbers ?? [];
  if (clientParts.length > 0) {
    for (const part of clientParts) {
      colonEntries.push({
        label: formatQuickBooksPartNumberLabel(part.type),
        value: part.value,
      });
    }
  } else {
    const partNum = normalizeOptionalText(item.mfgPartNumber);
    if (partNum) {
      colonEntries.push({
        label: item.partNumberLabel ?? "Part #",
        value: partNum,
      });
    }
  }
  const metaLines = formatAlignedColonLines(
    colonEntries,
    QB_DESC_LABEL_MIN_WIDTH
  );

  const directMeta = item.deliveredDirect
    ? [
        "This item was delivered Direct",
        "",
        ...buildDirectDeliveryBlock().split("\n"),
      ]
    : [];
  const qualityClauses = normalizeOptionalText(item.specialNote || "");

  return [
    ...descriptionLines,
    ...metaLines,
    ...(directMeta.length > 0 ? ["", ...directMeta] : []),
    ...(qualityClauses ? ["", `Quality Clauses: ${qualityClauses}`] : []),
  ].join("\n");
};

/** Labels re-emitted as an aligned colon block (client price quote order). */
const ESTIMATE_DESC_META_LABELS = new Set(
  [
    "mfg",
    "source",
    "trade name",
    "alt mfg",
    "part #",
    "catalog #",
    "sku #",
    "model #",
    "nsn #",
    "actual part #",
    "client part #",
  ].map((s) => s.toLowerCase())
);

function stripEstimateMetaColonLines(lines: string[]): string[] {
  return lines.filter((line) => {
    const trimmed = line.trim();
    const m = trimmed.match(/^([^:]+):\s*(.*)$/);
    if (!m) return true;
    const label = m[1].replace(/\s+/g, " ").trim().toLowerCase();
    // Keep "Quality Clauses:" in the body path (emitted separately below).
    if (label === "quality clauses") return true;
    if (ESTIMATE_DESC_META_LABELS.has(label)) return false;
    // Also drop dynamic part-number labels like "Catalog #:" / "Sku #:".
    if (/^.+\s#$/.test(label)) return false;
    return true;
  });
}

/** True when a description line is (or begins with) the QB item name. */
function descriptionLineStartsWithItemName(
  line: string,
  itemName: string
): boolean {
  const name = normalizeOptionalText(itemName);
  const text = normalizeOptionalText(line);
  if (!name || !text || text.length < name.length) return false;
  if (text.slice(0, name.length).toLowerCase() !== name.toLowerCase()) {
    return false;
  }
  if (text.length === name.length) return true;
  // e.g. "qb_item — short name" / "qb_item - foo" / "qb_item: foo".
  // A plain word continuation ("Name Kit - Jumbo …") is real description text.
  return /^\s*[—–\-:|.]/.test(text.slice(name.length));
}

/**
 * Drop leading description lines that restate the QB item name or homework
 * short name (e.g. "qb_item — short name" or bare "short name") so Desc does
 * not duplicate item identity already shown elsewhere.
 */
function stripLeadingItemNameDescriptionLines(
  lines: string[],
  itemName: string,
  shortDescription?: string
): string[] {
  const out = lines.map(stripDescLabelPrefix).filter((line) => line.length > 0);
  while (out.length > 0) {
    const first = out[0];
    if (descriptionLineStartsWithItemName(first, itemName)) {
      out.shift();
      continue;
    }
    const shortName = normalizeOptionalText(shortDescription || "");
    if (
      shortName &&
      normalizeOptionalText(first).toLowerCase() === shortName.toLowerCase()
    ) {
      out.shift();
      continue;
    }
    break;
  }
  return out;
}

/** Remove a leading DESC: label left over from older estimate formatting. */
function stripDescLabelPrefix(line: string): string {
  return normalizeOptionalText(line).replace(/^desc:\s*/i, "");
}

/**
 * ItemAdd payload: SalesDesc / PurchaseDesc match estimate (and PO) product Desc.
 * Leaves other fields unchanged so estimate/PO builders still receive the raw body.
 */
export function withQuickBooksItemListDescriptions<
  T extends EstimateLineInput,
>(items: T[]): T[] {
  return items.map((item) => {
    const description = buildEstimateLineDescription(item);
    return {
      ...item,
      salesDescription: description,
      purchaseDescription: description,
    };
  });
}

/** Vendor / contact / order / part # for every row — one trailing estimate line (see vendorFooterLine). */
const buildCombinedEstimateVendorFooterDescription = (
  items: EstimateLineInput[]
): string => {
  const blocks = items
    .map((item, index) => buildVendorContactFooterBlock(item, index))
    .filter((block) => block.length > 0);
  if (blocks.length === 0) return "";
  return `\n\n${blocks.join("\n\n")}`;
};

/**
 * Builds the estimate `lines` payload exactly as queued for QuickBooks (same as {@link createEstimateForQuickBooks}).
 * @param customerName When set (e.g. Amentum Comet / dont_use1), description header PO# uses JAC01-{po}.
 */
export function buildEstimateLinesForQuickBooks(
  items: EstimateLineInput[],
  customerName?: string
): QuickBooksEstimateQueueLine[] {
  const productGroups: QuickBooksEstimateQueueLine[][] = items.map(
    (item, index) => {
      const lineItemLabel =
        normalizeOptionalText(item.lineItemLabel) ||
        `Line ${index + 1} - ${normalizeOptionalText(item.itemName) || "Item"}`;
      const sellTotal = item.salesPrice ?? 0;
      const lineOther2 =
        item.rfqLineNumber != null && item.rfqLineNumber > 0
          ? `Line ${item.rfqLineNumber}`
          : `Line ${index + 1}`;
      const productLine: QuickBooksEstimateQueueLine = {
        itemName: item.itemName,
        po: normalizeOptionalText(item.po) || undefined,
        description: buildEstimateLineDescription(item),
        qty: item.qty,
        // Quantity + exact sell Amount lets QuickBooks derive its internal Rate.
        // This avoids the conversion drift caused by cost Rate + MarkupRate and
        // preserves totals that require more than two unit-price decimals.
        amount: qbEstimateLineAmount(sellTotal),
        taxable: item.taxable === true ? true : undefined,
        other1: item.unitOfMeasure || "",
        other2: lineOther2,
        qbEstimateFooterSource: {
          vendorAndPhone: item.vendorAndPhone,
          contactAndEmail: item.contactAndEmail,
          vendorPhone: item.vendorPhone,
          vendorEmail: item.vendorEmail,
          orderConfirmation: item.orderConfirmation,
          mfgPartNumber: item.mfgPartNumber,
          vendorPartNumber: item.vendorPartNumber,
          modelSkuCatalog: item.modelSkuCatalog,
          lineItemLabel,
          footerSpecialNotes: item.footerSpecialNotes,
        },
      };
      const feeLines: QuickBooksEstimateQueueLine[] = (
        item.estimateFeeLines ?? []
      ).map((fee) => ({
        itemName: item.itemName,
        description: formatEstimateFeeDescription(fee),
        qty: fee.qty,
        amount: qbEstimateLineAmount(fee.amount),
        taxable: fee.taxable === true ? true : undefined,
        other1: item.unitOfMeasure || "",
        other2: lineOther2,
        estimateFeeLine: true,
      }));
      return [productLine, ...feeLines];
    }
  );

  // Spacers between product+fee groups (not between a product and its fees).
  const productLines: QuickBooksEstimateQueueLine[] = [];
  for (let i = 0; i < productGroups.length; i++) {
    productLines.push(...productGroups[i]);
    if (i < productGroups.length - 1) {
      productLines.push(buildProductLineSpacerLine());
    }
  }

  const vendorBlockDescription =
    buildCombinedEstimateVendorFooterDescription(items);
  const anchorItem = items.find((i) => normalizeOptionalText(i.itemName));
  const anchorItemName = anchorItem?.itemName?.trim() ?? "";
  let lines: QuickBooksEstimateQueueLine[] = productLines;
  if (vendorBlockDescription !== "" && anchorItemName) {
    const headerLine: QuickBooksEstimateQueueLine = {
      itemName: "",
      description: buildEstimateHeaderLineDescription(
        resolveEstimatePoFromItems(items),
        customerName
      ),
      other1: "",
      other2: "",
      vendorHeaderLine: true,
    };
    const footerLine: QuickBooksEstimateQueueLine = {
      itemName: "",
      description: buildEstimateFooterLineDescription(vendorBlockDescription),
      other1: "",
      other2: "",
      vendorFooterLine: true,
    };
    lines = [
      headerLine,
      buildEstimateHeaderSpacerLine(),
      ...productLines,
      buildEstimateFooterBlankSpacerLine(),
      footerLine,
    ];
  }
  lines = collapseEstimateVendorBlockLines(lines, customerName);
  return lines;
}

const CREDIT_CARD_CHARGE_FILL = "---------";
/** Space between charge-record columns (1/1000 em, ~6 spaces). */
const CREDIT_CARD_COLUMN_GAP = 6 * QB_DESC_PAD_WIDTH;

/** NBSP-pad so each text starts as close as possible to its x position (1/1000 em). */
const placeQbDescText = (cells: Array<{ text: string; x: number }>): string => {
  let out = "";
  let cursor = 0;
  for (const cell of cells) {
    const pads = Math.max(
      out ? 1 : 0,
      Math.round((cell.x - cursor) / QB_DESC_PAD_WIDTH)
    );
    out += QB_DESC_PAD.repeat(pads) + cell.text;
    cursor += pads * QB_DESC_PAD_WIDTH + measureQbDescText(cell.text);
  }
  return out;
};

/** Labels on one row, each value centered under its label on the next. */
function formatCenteredColumns(
  columns: Array<{ label: string; value: string }>
): string[] {
  const labels: Array<{ text: string; x: number }> = [];
  const values: Array<{ text: string; x: number }> = [];
  let x = 0;
  for (const { label, value } of columns) {
    const labelWidth = measureQbDescText(label);
    const valueWidth = measureQbDescText(value);
    const width = Math.max(labelWidth, valueWidth);
    labels.push({ text: label, x: x + (width - labelWidth) / 2 });
    values.push({ text: value, x: x + (width - valueWidth) / 2 });
    x += width + CREDIT_CARD_COLUMN_GAP;
  }
  return [placeQbDescText(labels), placeQbDescText(values)];
}

/**
 * Preview only — qbwc adds the same block when it sends a Credit Card terms
 * (NASA) estimate: at the bottom of the vendor footer (own row when there is none).
 */
export function withCreditCardChargePreviewLines(
  lines: QuickBooksEstimateQueueLine[],
  customerName: string | null | undefined,
  buyerCardLast4: string | null | undefined
): QuickBooksEstimateQueueLine[] {
  if (
    quickBooksEstimateTermsForCustomer(customerName) !==
    QUICKBOOKS_ESTIMATE_TERMS_NASA
  ) {
    return lines;
  }
  const productCount = lines.filter(
    (l) =>
      normalizeOptionalText(l.itemName) &&
      !l.vendorHeaderLine &&
      !l.vendorFooterLine &&
      !l.estimateHeaderSpacerLine &&
      !l.estimateFooterSpacerLine &&
      !l.estimateFooterBlankSpacerLine &&
      !l.productLineSpacerLine &&
      !l.estimateFeeLine
  ).length;
  if (productCount === 0) return lines;
  const chargeDesc = [
    productCount > 1
      ? "These items were charged on Credit Card"
      : "This item was charged on Credit Card",
    ...formatCenteredColumns([
      { label: "Date Delivered", value: CREDIT_CARD_CHARGE_FILL },
      { label: "Date Charged", value: CREDIT_CARD_CHARGE_FILL },
      {
        label: "Last 4 Credit Card",
        value: normalizeOptionalText(buyerCardLast4) || CREDIT_CARD_CHARGE_FILL,
      },
    ]),
  ].join("\n");
  const footerIdx = lines.findIndex((l) => l.vendorFooterLine);
  if (footerIdx < 0) {
    return [
      ...lines,
      {
        itemName: "",
        description: " ",
        productLineSpacerLine: true,
        creditCardChargeLine: true,
      },
      { itemName: "", description: chargeDesc, creditCardChargeLine: true },
    ];
  }
  const footer = lines[footerIdx];
  const footerBody = footer.description
    .replace(new RegExp(`\\r?\\n${QB_ESTIMATE_FOOTER_DESC_MARKER}\\s*$`, "i"), "")
    .trimEnd();
  const out = [...lines];
  out[footerIdx] = {
    ...footer,
    description: footerBody ? `${footerBody}\n\n${chargeDesc}` : chargeDesc,
  };
  return out;
}

export const queueItemsForQuickBooks = async (
  rfqReference: string,
  items: object[]
): Promise<QbSyncQueueResponse> => {
  try {
    const response = await http.post(`${QB_SYNC_API_URL}/queue`, {
      rfqReference,
      items,
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to queue items for QuickBooks sync"
      );
    }
    throw error;
  }
};

export interface QbSyncSummaryEntry {
  total: number;
  pending: number;
  syncing: number;
  synced: number;
  error: number;
}

export type QbSyncSummary = Record<string, QbSyncSummaryEntry>;

export const createEstimateForQuickBooks = async (
  rfqReference: string,
  customerName: string,
  agentName: string,
  buyerName: string,
  dueDate: string | undefined,
  items: EstimateLineInput[],
  /** When set, qbwc runs EstimateQuery + EstimateMod to append lines to this TxnID. */
  existingEstimateTxnId?: string | null,
  /** PO due date — month/day tail on estimate FOB header; also estimate DueDate. */
  poDueDateIso?: string | undefined,
  /** QB estimate Extra header (DataExt); defaults to display form of rfqReference. */
  rfqDisplayNumber?: string,
  /**
   * Customer PO this estimate belongs to (one QB estimate per PO). Omit for the
   * legacy single combined estimate per RFQ.
   */
  estimatePoKey?: string,
  /**
   * Per-line send: add these lines to the estimate already queued for this PO.
   * Lines that were not part of this request stay on that estimate.
   */
  appendEstimateLines?: boolean
): Promise<QbSyncQueueResponse> => {
  try {
    const poValues = items
      .map((item) => normalizeOptionalText(item.po))
      .filter((value) => value.length > 0);
    const uniquePoValues = poValues.filter(
      (value, index, arr) => arr.indexOf(value) === index
    );
    const poNumbers = uniquePoValues.join(", ");
    const poNumberRaw = uniquePoValues[0] || "";
    const { poNumber, estimateRefNumber, otherHeader } =
      resolveQuickBooksEstimatePoHeaders(customerName, poNumberRaw);

    const lines = buildEstimateLinesForQuickBooks(items, customerName);
    const paymentTerms = quickBooksEstimateTermsForCustomer(customerName);
    // QuickBooks estimate DueDate = customer PO due date (not RFQ need-by).
    const dueDateForQb = poDueDateIso || dueDate;
    const formattedDueDate = dueDateForQb
      ? dueDateForQb.split("T")[0]
      : undefined;
    const fobDate = formatFobDate(poDueDateIso || dueDate);
    const buyerFirst = buyerFirstNameFromDisplayName(buyerName);
    const normalizedAgent = normalizeOptionalText(agentName);
    const repForQuickBooks = QUICKBOOKS_ESTIMATE_REP_NAME.trim();
    const fob = [buyerFirst, normalizeOptionalText(fobDate)]
      .filter((value) => value.length > 0)
      .join(" ");
    const customerForQb = normalizeCustomerJobForQuickBooks(customerName);
    const rfqNumberForQb = quickBooksRfqDisplayNumber(
      (rfqDisplayNumber ?? "").trim() || rfqReference
    );
    const estimateItem: Record<string, unknown> = {
      syncType: "Estimate",
      customer: customerForQb,
      agent: normalizedAgent,
      /** QuickBooks SalesRepRef / REP — fixed display name in QB (see QUICKBOOKS_ESTIMATE_REP_NAME). */
      rep: repForQuickBooks,
      /** Legacy alias; qbwc reads this after `rep` for SalesRepRef */
      buyer: repForQuickBooks,
      rfqNumber: rfqNumberForQb,
      /** qbwc → Estimate TxnDate (header date); not the RFQ need-by DueDate. */
      txnDate: formatLocalDateYyyyMmDd(),
      dueDate: formattedDueDate,
      fob,
      paymentTerms,
      poNumber,
      poNumbers,
      estimateRefNumber,
      /** Amentum Comet: JAC01-{po} (prefix added if missing); otherwise same as poNumber. */
      otherHeader,
      lines,
    };
    const trimmedTxn = (existingEstimateTxnId ?? "").trim();
    if (trimmedTxn) {
      estimateItem.estimateTxnId = trimmedTxn;
    }
    const trimmedPoKey = (estimatePoKey ?? "").trim();
    if (trimmedPoKey) {
      estimateItem.estimatePoKey = trimmedPoKey;
    }
    if (appendEstimateLines) {
      estimateItem.appendEstimateLines = true;
    }
    const response = await http.post(`${QB_SYNC_API_URL}/queue`, {
      rfqReference,
      items: [estimateItem],
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to queue estimate for QuickBooks sync"
      );
    }
    throw error;
  }
};

/**
 * Queue bills for an RFQ after all current QuickBooks purchase orders are synced.
 * Bills are built server-side from PO queue rows (with poTxnId linkage).
 */
export const queueBillsForQuickBooks = async (
  rfqReference: string
): Promise<QbQueueBillsResponse> => {
  try {
    const response = await http.post(`${QB_SYNC_API_URL}/bills`, {
      rfqReference,
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to queue bills for QuickBooks sync"
      );
    }
    throw error;
  }
};

/** @deprecated Prefer {@link queueBillsForQuickBooks}; this builds unlinked bill payloads. */
export const createBillsForQuickBooks = async (
  rfqReference: string,
  dueDate: string | undefined,
  items: BillLineInput[]
): Promise<QbSyncQueueResponse> => {
  try {
    assertQuickBooksVendorLabelsPresent(items, undefined);
    const billTransactions = buildBillQueueTransactions(
      rfqReference,
      dueDate,
      items
    );
    if (billTransactions.length === 0) {
      throw new Error("No items available to create bill");
    }

    const response = await http.post(`${QB_SYNC_API_URL}/queue`, {
      rfqReference,
      items: billTransactions,
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to queue bills for QuickBooks sync"
      );
    }
    throw error;
  }
};

/** Builds bill queue payload exactly as queued for QuickBooks (one bill per vendor). */
export function buildBillQueueTransactions(
  rfqReference: string,
  dueDate: string | undefined,
  items: BillLineInput[]
): QuickBooksBillQueueTransaction[] {
  if (items.length === 0) {
    return [];
  }

  const groupedByVendor = new Map<string, BillLineInput[]>();
  for (const item of items) {
    const vendorName = quickBooksVendorGroupKey(item);

    const existing = groupedByVendor.get(vendorName) || [];
    existing.push(item);
    groupedByVendor.set(vendorName, existing);
  }

  if (groupedByVendor.size === 0) {
    return [];
  }

  const formattedDueDate = dueDate ? dueDate.split("T")[0] : undefined;
  return Array.from(groupedByVendor.entries()).map(([vendor, vendorItems]) => {
    const poValues = vendorItems
      .map((item) => normalizeOptionalText(item.po))
      .filter((value) => value.length > 0);
    const uniquePoValues = poValues.filter(
      (value, index, arr) => arr.indexOf(value) === index
    );
    const poNumber = uniquePoValues[0] || "";
    const paymentTerms =
      vendorItems
        .map((item) => normalizeOptionalText(item.paymentTerms))
        .find((value) => value.length > 0) || "";

    const lines: QuickBooksBillQueueLine[] = vendorItems.map((item) => ({
      itemName: item.itemName,
      description: item.purchaseDescription || "",
      qty: item.qty,
      cost: qbPurchaseOrderUnitCost(
        item.cost ?? 0,
        item.shippingFee ?? 0,
        item.qty
      ),
    }));

    return {
      syncType: "Bill",
      vendor,
      rfqNumber: quickBooksRfqDisplayNumber(rfqReference),
      dueDate: formattedDueDate,
      paymentTerms,
      terms: paymentTerms,
      poNumber,
      lines,
    };
  });
}

export const createPurchaseOrdersForQuickBooks = async (
  rfqReference: string,
  items: PurchaseOrderLineInput[],
  /** Fallback for QB PO header FOB when buyer name is empty; capped to 13 characters. */
  assignedToForFob?: string,
  /** QB Customer:Job FullName for PO; normalized via {@link normalizeCustomerJobForQuickBooks} before queue. */
  customerJobForPurchaseOrder?: string,
  /** RFQ buyer display name — preferred QB PO FOB (first name, 13-char cap). */
  buyerNameForFob?: string
): Promise<QbSyncQueueResponse> => {
  try {
    assertQuickBooksVendorLabelsPresent(items, undefined);
    const poTransactions = buildPurchaseOrderQueueTransactions(
      rfqReference,
      items,
      assignedToForFob,
      customerJobForPurchaseOrder,
      buyerNameForFob
    );
    if (poTransactions.length === 0) {
      throw new Error("No items available to create purchase order");
    }

    const response = await http.post(`${QB_SYNC_API_URL}/queue`, {
      rfqReference,
      items: poTransactions,
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data ||
          "Failed to queue purchase orders for QuickBooks sync"
      );
    }
    throw error;
  }
};

export const fetchQbSyncSummary = async (): Promise<QbSyncSummary> => {
  try {
    const response = await http.get(`${QB_SYNC_API_URL}/summary`);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to fetch QB sync summary"
      );
    }
    throw error;
  }
};

export const fetchQbSyncStatus = async (
  rfqReference: string
): Promise<QbSyncStatusResponse> => {
  try {
    const response = await http.get(
      `${QB_SYNC_API_URL}/status/${encodeURIComponent(rfqReference)}`
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to fetch QB sync status");
    }
    throw error;
  }
};

export interface QbSyncRequeueResponse {
  rfqReference: string;
  reset: number;
}

/** Reset all errored qb_sync_queue rows for an RFQ back to pending. */
export const requeueRfqForQuickBooks = async (
  rfqReference: string
): Promise<QbSyncRequeueResponse> => {
  try {
    const response = await http.post(`${QB_SYNC_API_URL}/requeue`, {
      rfqReference,
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to re-queue RFQ for QuickBooks sync"
      );
    }
    throw error;
  }
};

export interface QbResetEstimatesResponse {
  rfqReference: string;
  removed: number;
  reset: number;
}

/**
 * Admin only: unlock an RFQ's estimate lines so they can be sent to QuickBooks again.
 * Re-sending updates the estimate already in QuickBooks; purchase orders and bills are untouched.
 */
export const resetRfqQuickBooksEstimates = async (
  rfqReference: string
): Promise<QbResetEstimatesResponse> => {
  try {
    const response = await http.post(`${QB_SYNC_API_URL}/reset-estimates`, {
      rfqReference,
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to reset QuickBooks send state"
      );
    }
    throw error;
  }
};

/**
 * Queue a one-shot QuickBooks CustomerQuery (all customers) to fill billAddress / shipAddress.
 * Does not create or modify estimates, POs, or bills.
 */
export const syncAllCustomerAddressesFromQuickBooks = async (): Promise<{
  queued: number;
  id: number;
}> => {
  try {
    const response = await http.post(`${QB_SYNC_API_URL}/sync-customer-addresses`, {});
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to queue customer address sync"
      );
    }
    throw error;
  }
};

export const downloadChromeExtensionZip = async (): Promise<Blob> => {
  try {
    const response = await http.get(
      `${API_BASE_URL}/download/chrome-extension`,
      {
        responseType: "blob",
      }
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to download chrome extension zip"
      );
    }
    throw error;
  }
};

export const createQuote = async (quote: CreateQuoteRequest): Promise<Rfq> => {
  try {
    const response = await http.post(RFQ_API_URL, quote);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to create quote");
    }
    throw error;
  }
};

export const duplicateRfq = async (rfqId: number): Promise<Rfq> => {
  try {
    const response = await http.post(`${RFQ_API_URL}/${rfqId}/duplicate`);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to duplicate RFQ");
    }
    throw error;
  }
};

export const deleteRfq = async (rfqId: number): Promise<void> => {
  try {
    await http.delete(`${RFQ_API_URL}/${rfqId}`);
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to delete RFQ");
    }
    throw error;
  }
};

export interface ChatGptConversationMessage {
  role: "system" | "user" | "assistant";
  content: string;
  timestamp: string;
}

export interface ContinueChatGptConversationResponse {
  analysis: string;
  conversation: ChatGptConversationMessage[];
  updated: boolean;
  rfq: Rfq;
}

export const continueChatGptConversation = async (
  rfqId: number,
  message: string
): Promise<ContinueChatGptConversationResponse> => {
  try {
    const response = await http.post(
      `${RFQ_API_URL}/${rfqId}/continue-chatgpt`,
      { message }
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to continue ChatGPT conversation"
      );
    }
    throw error;
  }
};

export const updateRfq = async (
  id: number,
  quote: UpdateQuoteRequest
): Promise<Rfq> => {
  try {
    const response = await http.put(`${RFQ_API_URL}/${id}`, quote);
    return response.data;
  } catch (error) {
    throw new Error(apiErrorMessage(error, "Failed to update quote"));
  }
};

export const undoRfqHistoryChange = async (
  rfqId: number,
  historyId: number,
  changeIndex: number
): Promise<void> => {
  try {
    await http.post(`${RFQ_API_URL}/${rfqId}/history/${historyId}/undo`, {
      changeIndex,
    });
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to undo history change"
      );
    }
    throw error;
  }
};

export const HOMEWORK_API_URL = `${API_BASE_URL}/homework`;

export const approveHomework = async (
  homeworkId: number,
  approve: boolean
): Promise<RfqHomework> => {
  try {
    const response = await http.put(
      `${HOMEWORK_API_URL}/approve/${homeworkId}`,
      { approve }
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to create quote from homework"
      );
    }
    throw error;
  }
};

export const copyHomework = async (
  homeworkId: number
): Promise<RfqHomework> => {
  try {
    const response = await http.post(`${HOMEWORK_API_URL}/copy/${homeworkId}`);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to copy homework");
    }
    throw error;
  }
};

export const deleteHomework = async (homeworkId: number): Promise<Rfq> => {
  try {
    const response = await http.delete(`${HOMEWORK_API_URL}/${homeworkId}`);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to delete homework");
    }
    throw error;
  }
};

export const addQuoteItem = async (
  rfqId: number,
  item: RfqItem
): Promise<RfqItem> => {
  try {
    const response = await http.post(`${HOMEWORK_ITEM_API_URL}/${rfqId}`, item);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to add item to quote");
    }
    throw error;
  }
};

export const updateQuoteItem = async (
  itemId: number,
  item: Partial<RfqItem>
): Promise<RfqItem> => {
  try {
    const response = await http.put(`${HOMEWORK_ITEM_API_URL}/${itemId}`, item);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to update quote item");
    }
    throw error;
  }
};

export interface HomeworkShoppingTabPersistLine {
  itemId: number;
  vendorUrls: string[];
  focusedTabUrl?: string | null;
}

export const updateHomeworkShoppingTabs = async (
  homeworkId: number,
  updates: HomeworkShoppingTabPersistLine[]
): Promise<RfqHomework> => {
  try {
    // Background persist from the Chrome extension while shopping — do not
    // flash the global loading overlay on every tab URL save.
    const response = await http.patch<RfqHomework>(
      `${HOMEWORK_API_URL}/shopping-tabs`,
      { homeworkId, updates },
      { skipGlassPane: true } as Parameters<typeof http.patch>[2]
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to save shopping tab URLs"
      );
    }
    throw error;
  }
};

export const deleteQuoteItem = async (itemId: number): Promise<Rfq> => {
  try {
    const response = await http.delete(`${HOMEWORK_ITEM_API_URL}/${itemId}`);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to remove item from quote"
      );
    }
    throw error;
  }
};

export const HOMEWORK_ITEM_API_URL = `${API_BASE_URL}/homework-item`;
export const PART_NUMBER_API_URL = `${API_BASE_URL}/part-number`;
export const VENDOR_QUOTE_ITEM_NUMBER_API_URL = `${API_BASE_URL}/vendor-quote-item-number`;

export const sortItems = async (
  homeworkId: number,
  itemIds: number[]
): Promise<Rfq> => {
  try {
    const response = await http.post(
      `${HOMEWORK_ITEM_API_URL}/${homeworkId}/sort`,
      { itemIds }
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to sort items");
    }
    throw error;
  }
};

export const importLineItems = async (
  targetHomeworkId: number,
  sourceItemIds: number[]
): Promise<Rfq> => {
  try {
    const response = await http.post(
      `${HOMEWORK_ITEM_API_URL}/${targetHomeworkId}/import`,
      { sourceItemIds }
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to import line items");
    }
    throw error;
  }
};

export interface RfqReference {
  rfqId: number;
  referenceNum: string;
}

export const getRfqReferences = async (
  rfqId: number
): Promise<{ referencedBy: RfqReference[] }> => {
  try {
    const response = await http.get(`${RFQ_API_URL}/${rfqId}/references`);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to get RFQ references");
    }
    throw error;
  }
};

export const deletePartNumber = async (
  partNumberId: number
): Promise<RfqItemNumber | null> => {
  try {
    const response = await http.delete(
      `${PART_NUMBER_API_URL}/${partNumberId}`
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to delete part number");
    }
    throw error;
  }
};

export interface AddPartNumberRequest {
  homeworkItemId: number;
  itemNumber: RfqItemNumber;
}

export const addPartNumber = async (
  request: AddPartNumberRequest
): Promise<RfqItemNumber> => {
  try {
    const response = await http.post(`${PART_NUMBER_API_URL}`, request);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to add part number");
    }
    throw error;
  }
};

export const updatePartNumber = async (
  partNumber: RfqItemNumber
): Promise<RfqItemNumber> => {
  try {
    const response = await http.patch(
      `${PART_NUMBER_API_URL}/${partNumber.id}`,
      {
        type: partNumber.type,
        value: partNumber.value,
        preferred: partNumber.preferred,
      }
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to update part number");
    }
    throw error;
  }
};

export interface AddVendorItemNumberRequest {
  vendorQuoteId: number;
  type: string;
  value: string;
}

export const addVendorItemNumber = async (
  request: AddVendorItemNumberRequest
): Promise<VendorItemNumber> => {
  try {
    const response = await http.post(VENDOR_QUOTE_ITEM_NUMBER_API_URL, request);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to add vendor item number"
      );
    }
    throw error;
  }
};

export const updateVendorItemNumber = async (
  itemNumber: VendorItemNumber
): Promise<VendorItemNumber> => {
  try {
    const response = await http.patch(
      `${VENDOR_QUOTE_ITEM_NUMBER_API_URL}/${itemNumber.id}`,
      {
        type: itemNumber.type,
        value: itemNumber.value,
      }
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to update vendor item number"
      );
    }
    throw error;
  }
};

export const deleteVendorItemNumber = async (
  itemNumberId: number
): Promise<null> => {
  try {
    await http.delete(`${VENDOR_QUOTE_ITEM_NUMBER_API_URL}/${itemNumberId}`);
    return null;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to delete vendor item number"
      );
    }
    throw error;
  }
};

export interface QuoteMarkupUpdate {
  id: number;
  fixedPriceType: FixedPriceType;
  fixedPrice: number;
  unitPriceSigFig: number;
  /** Mark Up % for Included fees; null leaves fees unchanged. */
  feeMarkup: number | null;
}

export const updatePriceMarkup = async (
  itemId: number,
  quotes: QuoteMarkupUpdate[]
): Promise<{ id: number; quotes: QuoteMarkupUpdate[] }> => {
  try {
    const response = await http.put(
      `${HOMEWORK_ITEM_API_URL}/${itemId}/markup`,
      { quotes }
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to update requested item markup"
      );
    }
    throw error;
  }
};

export const getQuoteHistory = async (
  itemId: number,
  options?: { signal?: AbortSignal; needles?: string[] }
): Promise<QuoteHistory[]> => {
  try {
    const needles = (options?.needles ?? [])
      .map((n) => n.trim().toLowerCase())
      .filter(Boolean);
    const response = await http.get(
      `${HOMEWORK_ITEM_API_URL}/${itemId}/history`,
      {
        signal: options?.signal,
        skipGlassPane: true,
        ...(needles.length > 0 ? { params: { needle: needles } } : {}),
      } as Parameters<typeof http.get>[1]
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      if (
        error.code === "ERR_CANCELED" ||
        error.name === "CanceledError" ||
        options?.signal?.aborted
      ) {
        throw error;
      }
      throw new Error(error.response?.data || "Failed to fetch part history");
    }
    throw error;
  }
};

export type QbCatalogSearchHit = {
  id: number;
  itemName: string;
  description: string;
  itemType: string;
  activeStatus: string | null;
  preferredVendor: string | null;
  unitOfMeasure: string;
  salesPrice: number | null;
  cost: number | null;
  quantity: number | null;
  mfgName: string | null;
  vendorAndPhone: string | null;
  partWeSold: string | null;
  mpn: string | null;
  modelSku: string | null;
  nsn: string | null;
  specialNote: string | null;
  csvRfq: string | null;
  orderConfirmation: string | null;
  poNumber: string | null;
  shippingFee: number | null;
};

export const searchQbCatalog = async (
  q: string,
  limit = 25
): Promise<QbCatalogSearchHit[]> => {
  try {
    const response = await http.get(
      `${HOMEWORK_ITEM_API_URL}/qb-catalog/search`,
      {
        params: { q, limit },
        skipGlassPane: true,
      } as Parameters<typeof http.get>[1]
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to search QB item catalog");
    }
    throw error;
  }
};

export type QbCatalogPagedResponse = {
  items: QbCatalogSearchHit[];
  total: number;
};

export const getQbCatalogPaged = async (
  skip: number,
  take: number,
  q = ""
): Promise<QbCatalogPagedResponse> => {
  try {
    const response = await http.get(
      `${HOMEWORK_ITEM_API_URL}/qb-catalog/paged`,
      { params: { skip, take, q: q || undefined } }
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to list QB item catalog");
    }
    throw error;
  }
};

export const getQbCatalogItemHistory = async (
  qbItemId: number
): Promise<QuoteHistory[]> => {
  try {
    const response = await http.get(
      `${HOMEWORK_ITEM_API_URL}/qb-catalog/${qbItemId}/history`
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to fetch QB item history"
      );
    }
    throw error;
  }
};

export const VENDOR_QUOTE_API_URL = `${API_BASE_URL}/vendor-quote`;
export const FEE_API_URL = `${API_BASE_URL}/fee`;

export interface AddFeeRequest {
  vendorQuoteId: number;
  feeType: string;
  description?: string;
  vendorFee?: number | null;
  customerFee?: number | null;
  isPercentMarkup?: boolean;
  qty?: number;
  isHidden?: boolean;
  ratePercent?: number | null;
}

export const addFee = async (request: AddFeeRequest): Promise<Fee> => {
  try {
    const response = await http.post(FEE_API_URL, request);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to add vendor fee");
    }
    throw error;
  }
};

export const updateFee = async (fee: Fee): Promise<Fee> => {
  try {
    const response = await http.patch(`${FEE_API_URL}/${fee.id}`, {
      feeType: fee.feeType,
      description: fee.description,
      vendorFee: fee.vendorFee,
      customerFee: fee.customerFee,
      isPercentMarkup: fee.isPercentMarkup,
      qty: fee.qty,
      isHidden: fee.isHidden,
      ratePercent: fee.ratePercent ?? null,
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to update vendor fee");
    }
    throw error;
  }
};

export const deleteFee = async (feeId: number): Promise<null> => {
  try {
    await http.delete(`${FEE_API_URL}/${feeId}`);
    return null;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to delete vendor fee");
    }
    throw error;
  }
};

export const selectVendorQuote = async (
  itemId: number,
  quoteId?: number | null
): Promise<{
  id: number;
  selectedVendorQuoteId?: number;
}> => {
  try {
    const response = await http.put(
      `${HOMEWORK_ITEM_API_URL}/${itemId}/select-quote/${quoteId || 0}`
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to select vendor for requested item"
      );
    }
    throw error;
  }
};

export const createVendorQuote = async (
  itemId: number,
  quote: VendorQuote,
  selectForCustomerQuote: boolean
): Promise<SaveVendorQuoteResponse> => {
  try {
    const response = await http.post<SaveVendorQuoteResponse>(
      VENDOR_QUOTE_API_URL,
      {
        ...quote,
        requestedItemId: itemId,
        selectForCustomerQuote,
      },
      { skipGlassPane: true } as Parameters<typeof http.post>[2]
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to create requested item vendor"
      );
    }
    throw error;
  }
};

export interface CreateVendorRfqRequest {
  homeworkId: number;
  vendorId: number;
  microsoft365EmailId: string;
  sentDate?: string;
}

export interface VendorRfq {
  id: number;
  homeworkId: number;
  vendorId: number;
  vendorRfqSentDate: Date;
  vendorRfqEmailMessageId: string;
  vendorRfqSentByUserId: number;
}

export const createVendorRfq = async (
  request: CreateVendorRfqRequest
): Promise<VendorRfq> => {
  try {
    const response = await http.post(`${API_BASE_URL}/vendor-rfq`, request);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to record vendor RFQ sent metadata"
      );
    }
    throw error;
  }
};

export interface CreateVendorQuoteRfqEmailsRequest {
  requestedItemIds: number[];
  vendorId: number;
  microsoft365EmailId: string;
  recipientEmails?: string[];
  sentDate?: string;
}

export const createVendorQuoteRfqEmails = async (
  request: CreateVendorQuoteRfqEmailsRequest
): Promise<VendorQuoteRfqEmail[]> => {
  try {
    const response = await http.post(
      `${API_BASE_URL}/vendor-quote-rfq-email`,
      request
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data ||
          "Failed to record vendor quote RFQ emails for line items"
      );
    }
    throw error;
  }
};

export interface RecordCustomerQuoteEmailSentRequest {
  microsoft365EmailId: string;
  sentDate?: string;
  /** Non-inline quote form files to archive in SharePoint "2026 RFQ". */
  quoteAttachments?: Array<{
    name: string;
    contentType: string;
    contentBytes: string;
    isInline?: boolean;
  }>;
}

export const recordCustomerQuoteEmailSent = async (
  homeworkId: number,
  request: RecordCustomerQuoteEmailSentRequest
): Promise<RfqHomework> => {
  try {
    const response = await http.put(
      `${HOMEWORK_API_URL}/customer-quote-email-sent/${homeworkId}`,
      request
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to record customer quote email metadata"
      );
    }
    throw error;
  }
};

export type QuoteResponseFieldKind =
  | "unit_price"
  | "total_price"
  | "extended_price"
  | "lead_time"
  | "small_business"
  | "woman_owned"
  | "other";

export interface DetectedQuoteResponseField {
  id: string;
  label: string;
  kind: QuoteResponseFieldKind;
  source: "email_body" | "attachment";
  attachmentId?: string;
  attachmentName?: string;
  hint?: string;
  lineItemHint?: string;
  lineNumber?: number;
  homeworkItemId?: number;
  draftedValue?: string;
}

export interface DetectQuoteResponseFieldsResponse {
  responseMode: "email_body" | "attachment_form" | "both" | "none";
  fields: DetectedQuoteResponseField[];
  returnAttachments: Array<{
    attachmentId: string;
    name: string;
    reason: string;
    format?: "pdf" | "docx" | "xlsx" | "other";
  }>;
  hasLinkedEmail: boolean;
}

export const detectQuoteResponseFields = async (
  homeworkId: number
): Promise<DetectQuoteResponseFieldsResponse> => {
  try {
    const response = await http.post(
      `${HOMEWORK_API_URL}/${homeworkId}/detect-quote-response-fields`,
      undefined,
      { skipGlassPane: true } as Parameters<typeof http.post>[2]
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to detect quote response fields"
      );
    }
    throw error;
  }
};

export interface QuoteResponseFillFieldInput {
  id: string;
  label: string;
  kind: QuoteResponseFieldKind;
  value: string;
  attachmentId?: string;
  attachmentName?: string;
  hint?: string;
  lineItemHint?: string;
  lineNumber?: number;
  homeworkItemId?: number;
}

export interface FilledQuoteAttachmentFile {
  name: string;
  contentType: string;
  contentBytes: string;
  sourceAttachmentId?: string;
  filled: boolean;
  message?: string;
  /** Eager PDF conversion of a filled docx/xlsx (same file, not a second attachment). */
  pdfName?: string;
  pdfContentBytes?: string;
}

export const fillQuoteResponseAttachments = async (
  homeworkId: number,
  fields: QuoteResponseFillFieldInput[],
  attachmentIds?: string[]
): Promise<FilledQuoteAttachmentFile[]> => {
  try {
    const response = await http.post(
      `${HOMEWORK_API_URL}/${homeworkId}/fill-quote-response-attachments`,
      { fields, attachmentIds },
      { skipGlassPane: true } as Parameters<typeof http.post>[2]
    );
    return response.data?.files ?? [];
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to fill quote response attachments"
      );
    }
    throw error;
  }
};

export const fillUploadedQuoteForms = async (
  homeworkId: number,
  fields: QuoteResponseFillFieldInput[],
  files: Array<{ name: string; contentType: string; contentBytes: string }>
): Promise<FilledQuoteAttachmentFile[]> => {
  try {
    const response = await http.post(
      `${HOMEWORK_API_URL}/${homeworkId}/fill-uploaded-quote-forms`,
      { fields, files },
      { skipGlassPane: true } as Parameters<typeof http.post>[2]
    );
    return response.data?.files ?? [];
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to fill uploaded quote forms"
      );
    }
    throw error;
  }
};

export const convertQuoteFormToPdf = async (
  homeworkId: number,
  file: {
    name: string;
    contentType: string;
    contentBytes: string;
    downloadName?: string;
  }
): Promise<{ name: string; contentType: string; contentBytes: string }> => {
  try {
    const response = await http.post(
      `${HOMEWORK_API_URL}/${homeworkId}/convert-quote-form-to-pdf`,
      file,
      { skipGlassPane: true } as Parameters<typeof http.post>[2]
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      const raw =
        typeof error.response?.data === "string"
          ? error.response.data
          : "Failed to convert quote form to PDF";
      throw new Error(friendlyQuoteFormPdfError(raw));
    }
    throw error;
  }
};

function friendlyQuoteFormPdfError(raw: string): string {
  if (
    /failed to launch the browser process|libatk|shared librar|puppeteer|chrome-linux|chrome_crashpad/i.test(
      raw
    )
  ) {
    return "Could not convert this form to PDF on the server. Use Original to download the filled file.";
  }
  return raw.trim() || "Failed to convert quote form to PDF";
}

export const updateVendorQuote = async (
  itemId: number,
  quote: VendorQuote,
  selectForCustomerQuote: boolean,
  options?: {
    approveQuoteChanges?: boolean;
    dismissQuoteChanges?: boolean;
    requireQuoteChangeApproval?: boolean;
    /**
     * Omit notes/fees from the payload. Defaults to true for approve/dismiss
     * requests; pass false when a regular edit save also approves pending
     * changes so fee edits still persist.
     */
    skipNestedCollections?: boolean;
    /**
     * Quote as the editor last loaded/saved it. When set, only fields that
     * differ are sent so a stale editor can't revert edits made elsewhere.
     */
    baseline?: { quote: VendorQuote; selectForCustomerQuote: boolean };
  }
): Promise<SaveVendorQuoteResponse> => {
  try {
    const approveQuoteChanges = Boolean(options?.approveQuoteChanges);
    const dismissQuoteChanges = Boolean(options?.dismissQuoteChanges);
    const requireQuoteChangeApproval = Boolean(
      options?.requireQuoteChangeApproval
    );
    const skipNestedCollections =
      options?.skipNestedCollections ??
      (approveQuoteChanges || dismissQuoteChanges);
    const baseline = options?.baseline;
    if (baseline) {
      const payload: Record<string, unknown> = {
        ...changedVendorQuoteScalars(quote, baseline.quote),
        requestedItemId: itemId,
        approveQuoteChanges,
        dismissQuoteChanges,
        requireQuoteChangeApproval,
      };
      if (selectForCustomerQuote !== baseline.selectForCustomerQuote) {
        payload.selectForCustomerQuote = selectForCustomerQuote;
      }
      if (
        vendorItemNumbersChanged(
          quote.vendorItemNumbers,
          baseline.quote.vendorItemNumbers
        )
      ) {
        payload.vendorItemNumbers = (quote.vendorItemNumbers ?? []).map(
          (itemNumber) => ({
            type: itemNumber.type,
            value: itemNumber.value,
            id: 0,
          })
        );
      }
      if (!skipNestedCollections) {
        if (notesChanged(quote.notes, baseline.quote.notes)) {
          payload.notes = quote.notes;
        }
        if (feesChanged(quote.fees, baseline.quote.fees)) {
          payload.fees = quote.fees;
        }
      }
      const response = await http.put<SaveVendorQuoteResponse>(
        VENDOR_QUOTE_API_URL,
        payload,
        { skipGlassPane: true } as Parameters<typeof http.put>[2]
      );
      return response.data;
    }

    const {
      notes: _notes,
      fees: _fees,
      quoteChangeSnapshot: _snapshot,
      quoteChangePendingApproval: _pending,
      quoteChangeApprovedById: _approvedBy,
      quoteChangeApprovedAt: _approvedAt,
      ...quoteFields
    } = quote;

    const payload: Record<string, unknown> = {
      ...quoteFields,
      requestedItemId: itemId,
      selectForCustomerQuote,
      approveQuoteChanges,
      dismissQuoteChanges,
      requireQuoteChangeApproval,
      vendorItemNumbers: (quote.vendorItemNumbers ?? []).map((itemNumber) => ({
        type: itemNumber.type,
        value: itemNumber.value,
        id: 0,
      })),
    };

    // Approval/dismiss-only saves should not rewrite notes/fees (avoids stale nested ids).
    if (!skipNestedCollections) {
      payload.notes = quote.notes;
      payload.fees = quote.fees;
    }

    const response = await http.put<SaveVendorQuoteResponse>(
      VENDOR_QUOTE_API_URL,
      payload,
      { skipGlassPane: true } as Parameters<typeof http.put>[2]
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      const data = error.response?.data;
      const msg =
        typeof data === "string" && data.trim()
          ? data
          : typeof data?.message === "string"
            ? data.message
            : typeof data?.error === "string"
              ? data.error
              : `Failed to update vendor quote (${error.response?.status ?? "network error"})`;
      throw new Error(msg);
    }
    throw error;
  }
};

export const deleteVendorQuote = async (
  quoteId: number
): Promise<{
  requestedItemId: number;
  selectedVendorQuoteId: number | null;
}> => {
  try {
    const response = await http.delete(`${VENDOR_QUOTE_API_URL}/${quoteId}`);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to delete vendor quote");
    }
    throw error;
  }
};

/** Run OpenAI web shopping for one RFQ line item; replaces prior candidates. */
export const shopRfqItem = async (
  rfqId: number,
  itemId: number,
  maxResults?: number
): Promise<RfqItemShoppingCandidate[]> => {
  try {
    const response = await http.post(
      `${RFQ_API_URL}/${rfqId}/items/${itemId}/shop`,
      maxResults != null ? { maxResults } : {}
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to shop RFQ item");
    }
    throw error;
  }
};

/**
 * Accept a shopping candidate as a vendor quote on the given RFQ item. The
 * backend matches the candidate's vendor name to an existing vendor and returns
 * the updated RFQ.
 */
export const acceptShoppingCandidate = async (
  rfqId: number,
  itemId: number,
  candidateId: number
): Promise<Rfq> => {
  try {
    const response = await http.post(
      `${RFQ_API_URL}/${rfqId}/items/${itemId}/shopping-candidates/${candidateId}/accept`
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to accept shopping candidate"
      );
    }
    throw error;
  }
};

/** Match candidate vendor to DB (exact → fuzzy → embedding); does not create vendor or quote. */
export const verifyShoppingCandidate = async (
  rfqId: number,
  itemId: number,
  candidateId: number
): Promise<ShoppingCandidateVendorResolution> => {
  try {
    const response = await http.post(
      `${RFQ_API_URL}/${rfqId}/items/${itemId}/shopping-candidates/${candidateId}/verify`
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to verify shopping candidate vendor"
      );
    }
    throw error;
  }
};

/** Read a vendor product page open in the shopping window; does not create a vendor or quote. */
export const extractProductPageForItem = async (
  rfqId: number,
  itemId: number,
  page: ProductPageSnapshot
): Promise<ExtractedProductPageResult> => {
  try {
    const response = await http.post(
      `${RFQ_API_URL}/${rfqId}/items/${itemId}/extract-product-page`,
      page
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Could not read that product page"
      );
    }
    throw error;
  }
};

/** Read How To Ship & Terms values from a vendor page; does not change the vendor quote. */
export const extractOrderPageForItem = async (
  rfqId: number,
  itemId: number,
  page: ProductPageSnapshot
): Promise<ExtractedOrderPageResult> => {
  try {
    const response = await http.post(
      `${RFQ_API_URL}/${rfqId}/items/${itemId}/extract-order-page`,
      page
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Could not read that page");
    }
    throw error;
  }
};

/** Save a PDF of a vendor page to the 2026 RFQ SharePoint library. */
export const saveOrderPagePdfForItem = async (
  rfqId: number,
  itemId: number,
  body: { pdfBase64: string; url: string; vendorName: string }
): Promise<OrderPagePdfUpload> => {
  try {
    const response = await http.post(
      `${RFQ_API_URL}/${rfqId}/items/${itemId}/order-page-pdf`,
      body
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Could not save the page PDF to SharePoint"
      );
    }
    throw error;
  }
};

/** Match or create vendor for a shopping candidate; does not create a vendor quote. */
export const ensureShoppingCandidateVendor = async (
  rfqId: number,
  itemId: number,
  candidateId: number
): Promise<ShoppingCandidateVendorResolution> => {
  try {
    const response = await http.post(
      `${RFQ_API_URL}/${rfqId}/items/${itemId}/shopping-candidates/${candidateId}/ensure-vendor`
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to ensure vendor for shopping candidate"
      );
    }
    throw error;
  }
};

// Customer Contact API functions
export const CUSTOMER_CONTACT_API_URL = `${API_BASE_URL}/customer-contact`;

export interface CreateContactRequest {
  role: CustomerContact["role"];
  name: string;
  email: string;
  phone: string;
  creditCard?: CreditCardInfo | null;
}

export interface UpdateContactRequest {
  role?: CustomerContact["role"];
  name?: string;
  email?: string;
  phone?: string;
  customerId?: number;
  /** Omit to leave the stored card unchanged; null clears it. */
  creditCard?: CreditCardInfo | null;
}

export const getCustomerContactCreditCard = async (
  id: number
): Promise<CreditCardInfo | null> => {
  try {
    const response = await http.get(
      `${CUSTOMER_CONTACT_API_URL}/${id}/credit-card`
    );
    return response.data || null;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to fetch credit card details"
      );
    }
    throw error;
  }
};

export const getCustomerContacts = async (
  customerId: number
): Promise<CustomerContact[]> => {
  try {
    const response = await http.get(
      `${CUSTOMER_CONTACT_API_URL}/customer/${customerId}`
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to fetch customer contacts"
      );
    }
    throw error;
  }
};

export const getCustomerContact = async (
  id: number
): Promise<CustomerContact> => {
  try {
    const response = await http.get(`${CUSTOMER_CONTACT_API_URL}/${id}`);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to fetch customer contact"
      );
    }
    throw error;
  }
};

export const createCustomerContact = async (
  customerId: number,
  contact: CreateContactRequest
): Promise<CustomerContact> => {
  try {
    const response = await http.post(
      `${CUSTOMER_CONTACT_API_URL}/${customerId}`,
      contact
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to create customer contact"
      );
    }
    throw error;
  }
};

export const updateCustomerContact = async (
  id: number,
  contact: UpdateContactRequest
): Promise<CustomerContact> => {
  try {
    const response = await http.put(
      `${CUSTOMER_CONTACT_API_URL}/${id}`,
      contact
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to update customer contact"
      );
    }
    throw error;
  }
};

export const deleteCustomerContact = async (id: number): Promise<string> => {
  try {
    const response = await http.delete(`${CUSTOMER_CONTACT_API_URL}/${id}`);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to delete customer contact"
      );
    }
    throw error;
  }
};

export const QC_CODE_API_URL = `${API_BASE_URL}/qc-code`;

export const getQCCodes = async (customerId?: number): Promise<QCCode[]> => {
  try {
    const response = await http.get(QC_CODE_API_URL, {
      params: customerId ? { customerId } : undefined,
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to fetch QC codes");
    }
    throw error;
  }
};

export const createQCCode = async (
  customerId: number,
  code: string,
  description: string
): Promise<QCCode> => {
  try {
    const response = await http.post(
      `${QC_CODE_API_URL}/customer/${customerId}`,
      { code, description }
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to create QC code");
    }
    throw error;
  }
};

export const updateQCCode = async (
  id: number,
  code: string,
  description: string
): Promise<QCCode> => {
  try {
    const response = await http.put(`${QC_CODE_API_URL}/${id}`, {
      code,
      description,
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to update QC code");
    }
    throw error;
  }
};

export const deleteQCCode = async (id: number): Promise<string> => {
  try {
    const response = await http.delete(`${QC_CODE_API_URL}/${id}`);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to delete QC code");
    }
    throw error;
  }
};

export const CATALOG_TYPE_API_URL = `${API_BASE_URL}/catalog-type`;

export const getCatalogTypes = async (
  category?: CatalogTypeCategory
): Promise<CatalogType[]> => {
  try {
    const response = await http.get(CATALOG_TYPE_API_URL, {
      params: category ? { category } : undefined,
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to fetch catalog types");
    }
    throw error;
  }
};

export const createCatalogType = async (
  category: CatalogTypeCategory,
  value: string,
  label?: string,
  colors?: {
    colorBg?: string | null;
    colorFg?: string | null;
    colorBorder?: string | null;
  }
): Promise<CatalogType> => {
  try {
    const response = await http.post(CATALOG_TYPE_API_URL, {
      category,
      value,
      label,
      ...colors,
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to create catalog type");
    }
    throw error;
  }
};

export const deleteCatalogType = async (id: number): Promise<string> => {
  try {
    const response = await http.delete(`${CATALOG_TYPE_API_URL}/${id}`);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to delete catalog type");
    }
    throw error;
  }
};

export const updateCatalogType = async (
  id: number,
  body: {
    label?: string;
    ratePercent?: number | null;
    flatAmount?: number | null;
    colorBg?: string | null;
    colorFg?: string | null;
    colorBorder?: string | null;
  }
): Promise<CatalogType> => {
  try {
    const response = await http.patch(`${CATALOG_TYPE_API_URL}/${id}`, body);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to update catalog type");
    }
    throw error;
  }
};

// Vendor Contact API functions
export const VENDOR_CONTACT_API_URL = `${API_BASE_URL}/vendor-contact`;

export interface CreateVendorContactRequest {
  role: VendorContact["role"];
  name: string;
  email: string;
  phone: string;
}

export interface UpdateVendorContactRequest {
  role?: VendorContact["role"];
  name?: string;
  email?: string;
  phone?: string;
}

export const getVendorContacts = async (
  vendorId: number
): Promise<VendorContact[]> => {
  try {
    const response = await http.get(
      `${VENDOR_CONTACT_API_URL}/vendor/${vendorId}`
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to fetch vendor contacts"
      );
    }
    throw error;
  }
};

export const getVendorContact = async (id: number): Promise<VendorContact> => {
  try {
    const response = await http.get(`${VENDOR_CONTACT_API_URL}/${id}`);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to fetch vendor contact");
    }
    throw error;
  }
};

export const createVendorContact = async (
  vendorId: number,
  contact: CreateVendorContactRequest
): Promise<VendorContact> => {
  try {
    const response = await http.post(
      `${VENDOR_CONTACT_API_URL}/vendor/${vendorId}`,
      contact
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to create vendor contact"
      );
    }
    throw error;
  }
};

export const updateVendorContact = async (
  id: number,
  contact: UpdateVendorContactRequest
): Promise<VendorContact> => {
  try {
    const response = await http.put(`${VENDOR_CONTACT_API_URL}/${id}`, contact);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to update vendor contact"
      );
    }
    throw error;
  }
};

export const deleteVendorContact = async (id: number): Promise<string> => {
  try {
    const response = await http.delete(`${VENDOR_CONTACT_API_URL}/${id}`);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to delete vendor contact"
      );
    }
    throw error;
  }
};

// Manufacturer Contact API functions
export const MANUFACTURER_CONTACT_API_URL = `${API_BASE_URL}/mfg-contact`;

export interface CreateManufacturerContactRequest {
  role: ManufacturerContact["role"];
  name: string;
  email: string;
  phone: string;
}

export interface UpdateManufacturerContactRequest {
  role?: ManufacturerContact["role"];
  name?: string;
  email?: string;
  phone?: string;
}

export const getManufacturerContacts = async (
  manufacturerId: number
): Promise<ManufacturerContact[]> => {
  try {
    const response = await http.get(
      `${MANUFACTURER_CONTACT_API_URL}/manufacturer/${manufacturerId}`
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to fetch manufacturer contacts"
      );
    }
    throw error;
  }
};

export const getManufacturerContact = async (
  id: number
): Promise<ManufacturerContact> => {
  try {
    const response = await http.get(`${MANUFACTURER_CONTACT_API_URL}/${id}`);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to fetch manufacturer contact"
      );
    }
    throw error;
  }
};

export const createManufacturerContact = async (
  manufacturerId: number,
  contact: CreateManufacturerContactRequest
): Promise<ManufacturerContact> => {
  try {
    const response = await http.post(
      `${MANUFACTURER_CONTACT_API_URL}/manufacturer/${manufacturerId}`,
      contact
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to create manufacturer contact"
      );
    }
    throw error;
  }
};

export const updateManufacturerContact = async (
  id: number,
  contact: UpdateManufacturerContactRequest
): Promise<ManufacturerContact> => {
  try {
    const response = await http.put(
      `${MANUFACTURER_CONTACT_API_URL}/${id}`,
      contact
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to update manufacturer contact"
      );
    }
    throw error;
  }
};

export const deleteManufacturerContact = async (
  id: number
): Promise<string> => {
  try {
    const response = await http.delete(`${MANUFACTURER_CONTACT_API_URL}/${id}`);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to delete manufacturer contact"
      );
    }
    throw error;
  }
};

// Email API
const EMAIL_API_URL = `${API_BASE_URL}/email`;

export interface GetEmailsResponse {
  emails: Email[];
}

export interface GetEmailsParams {
  folder?: "inbox" | "sent";
  archivedOnly?: boolean;
  /** Inclusive start, YYYY-MM-DD (required when archivedOnly is true) */
  startDate?: string;
  /** Inclusive end, YYYY-MM-DD (required when archivedOnly is true) */
  endDate?: string;
  /** Filter to a configured Outlook mailbox */
  mailbox?: string;
}

export interface MailboxOption {
  email: string;
  label: string;
}

export const getEmailMailboxes = async (): Promise<MailboxOption[]> => {
  try {
    const response = await http.get(`${EMAIL_API_URL}/mailboxes`);
    return response.data.mailboxes ?? [];
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to fetch mailboxes");
    }
    throw error;
  }
};

export const getEmails = async (
  params: GetEmailsParams = { folder: "inbox" }
): Promise<GetEmailsResponse> => {
  try {
    const query: Record<string, string> = {
      folder: params.folder ?? "inbox",
    };
    if (params.mailbox) {
      query.mailbox = params.mailbox;
    }
    if (params.archivedOnly) {
      query.archivedOnly = "true";
      if (params.startDate) query.startDate = params.startDate;
      if (params.endDate) query.endDate = params.endDate;
    }
    const response = await http.get(`${EMAIL_API_URL}`, { params: query });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to fetch emails");
    }
    throw error;
  }
};

export const getRfqEmails = async (rfqId: number): Promise<Email[]> => {
  try {
    const response = await http.get(`${RFQ_API_URL}/${rfqId}/emails`);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to fetch RFQ emails");
    }
    throw error;
  }
};

export const getEmail = async (id: number): Promise<Email> => {
  try {
    const response = await http.get(`${EMAIL_API_URL}/${id}`);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to fetch email");
    }
    throw error;
  }
};

export const getEmailByMessageId = async (
  messageId: string
): Promise<Email> => {
  try {
    const response = await http.get(
      `${EMAIL_API_URL}/by-message-id/${encodeURIComponent(messageId)}`
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to fetch email");
    }
    throw error;
  }
};

export const archiveEmail = async (
  id: number,
  archived: boolean
): Promise<Email> => {
  try {
    const response = await http.patch(`${EMAIL_API_URL}/${id}/archive`, {
      archived,
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to update email archive status"
      );
    }
    throw error;
  }
};

export type RfqApprovalEmailResult = {
  sharePointSaved: boolean;
  outlookMoved: boolean;
  archived: boolean;
  errors: string[];
  email: Email;
};

export type RfqApprovalEmailOptions = {
  saveToSharePoint?: boolean;
  moveToOutlook?: boolean;
  archive?: boolean;
};

/** Optional SharePoint save + Outlook "RFQs" move + soft-archive (RFQ approve). */
export const processEmailForRfqApproval = async (
  id: number,
  options: RfqApprovalEmailOptions = {}
): Promise<RfqApprovalEmailResult> => {
  try {
    const response = await http.post(
      `${EMAIL_API_URL}/${id}/rfq-approval`,
      options
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to process email for RFQ approval"
      );
    }
    throw error;
  }
};

export const reprocessEmail = async (id: number): Promise<Email> => {
  try {
    const response = await http.patch(`${EMAIL_API_URL}/${id}/process`, {});
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to process email");
    }
    throw error;
  }
};

export const archiveEmails = async (
  emailIds: number[],
  archived: boolean
): Promise<{ count: number }> => {
  try {
    const response = await http.patch(`${EMAIL_API_URL}/archive`, {
      emailIds,
      archived,
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to update email archive status"
      );
    }
    throw error;
  }
};

export const getAttachmentContent = async (
  emailId: number,
  attachmentId: string
): Promise<{ content: string; contentType: string; name: string }> => {
  try {
    const response = await http.get(
      `${EMAIL_API_URL}/${emailId}/attachments/${encodeURIComponent(attachmentId)}`,
      { skipGlassPane: true } as Parameters<typeof http.get>[1]
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to fetch attachment");
    }
    throw error;
  }
};

export type AttachmentOcrWord = {
  page: number;
  text: string;
  x: number;
  y: number;
  w: number;
  h: number;
};

export const getAttachmentOcr = async (
  emailId: number,
  attachmentId: string,
  opts?: { force?: boolean }
): Promise<{
  attachmentId: string;
  name: string;
  provider: string;
  pageCount: number;
  text: string;
  words: AttachmentOcrWord[];
  ocrAt: string | null;
}> => {
  try {
    const response = await http.get(
      `${EMAIL_API_URL}/${emailId}/attachments/${encodeURIComponent(attachmentId)}/ocr`,
      {
        params: opts?.force ? { force: "true" } : undefined,
        skipGlassPane: true,
      } as Parameters<typeof http.get>[1]
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to OCR attachment");
    }
    throw error;
  }
};

/** OCR an uploaded PDF for review highlighting (Azure Document Intelligence when configured). */
export const analyzeUploadedPdfOcr = async (
  file: Blob,
  fileName: string
): Promise<{
  text: string;
  words: AttachmentOcrWord[];
  pageCount: number;
  provider: string;
}> => {
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    const slice = bytes.subarray(i, i + chunk);
    for (let j = 0; j < slice.length; j++) {
      binary += String.fromCharCode(slice[j]);
    }
  }
  const contentBase64 = btoa(binary);
  try {
    const response = await http.post(
      `${API_BASE_URL}/pdf-ocr/analyze`,
      { contentBase64, fileName },
      { skipGlassPane: true } as Parameters<typeof http.post>[2]
    );
    if (response.data?.error) {
      throw new Error(response.data.error);
    }
    return {
      text: response.data.text || "",
      words: response.data.words || [],
      pageCount: response.data.pageCount || 1,
      provider: response.data.provider || "unknown",
    };
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data?.error ||
          error.response?.data ||
          "Failed to OCR uploaded PDF"
      );
    }
    throw error;
  }
};

export const downloadAttachment = async (
  emailId: number,
  attachmentId: string
): Promise<Blob> => {
  try {
    const response = await http.get(
      `${EMAIL_API_URL}/${emailId}/attachments/${encodeURIComponent(attachmentId)}/download`,
      { responseType: "blob" }
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      const data = error.response?.data;
      const message =
        data instanceof Blob ? (await data.text()).trim() : data;
      throw new Error(message || "Failed to download attachment");
    }
    throw error;
  }
};

/** Full message as an .eml file. */
export const downloadEmailEml = async (emailId: number): Promise<Blob> => {
  try {
    const response = await http.get(`${EMAIL_API_URL}/${emailId}/eml`, {
      responseType: "blob",
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      const data = error.response?.data;
      const message =
        data instanceof Blob ? (await data.text()).trim() : data;
      throw new Error(message || "Failed to download email");
    }
    throw error;
  }
};

export interface DuplicateEmailGroup {
  groupId: number;
  count: number;
  emails: Email[];
}

export interface SendEmailRequest {
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  body: string;
  contentType?: "text" | "html";
  /** Optional From mailbox; must be on the server allowlist. */
  mailbox?: string;
  attachments?: Array<{
    name: string;
    contentBytes: string;
    contentType: string;
    contentId?: string;
    isInline?: boolean;
  }>;
}

export interface SendEmailResponse {
  messageId: string;
  success: boolean;
}

function axiosErrorToMessage(error: AxiosError): string {
  const data = error.response?.data;
  if (typeof data === "string" && data.trim()) {
    return data;
  }
  if (data && typeof data === "object") {
    const o = data as Record<string, unknown>;
    if (typeof o.message === "string") return o.message;
    if (typeof o.error === "string") return o.error;
  }
  return error.message || "Failed to send email";
}

export const sendEmail = async (
  emailData: SendEmailRequest
): Promise<SendEmailResponse> => {
  try {
    const response = await http.post(`${EMAIL_API_URL}/send`, emailData);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(axiosErrorToMessage(error));
    }
    throw error;
  }
};

export interface ReplyEmailRequest {
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  body: string;
  contentType?: "text" | "html";
}

/** Threaded reply to a stored message (sent from the mailbox that received it). */
export const replyToEmail = async (
  emailId: number,
  emailData: ReplyEmailRequest
): Promise<SendEmailResponse> => {
  try {
    const response = await http.post(
      `${EMAIL_API_URL}/${emailId}/reply`,
      emailData
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(axiosErrorToMessage(error));
    }
    throw error;
  }
};

const PRICE_QUOTE_API_URL = `${API_BASE_URL}/price-quote`;
const PURCHASE_ORDER_API_URL = `${API_BASE_URL}/purchase-order`;

export const getPriceQuotes = async (params?: {
  status?: PriceQuoteStatus;
  includeDismissed?: boolean;
  rfqId?: number;
  /** When true, includes line items (needed for review matching). Default omit for work-queue polls. */
  includeLineItems?: boolean;
  /** Slim header fields for work-queue badge columns. */
  workQueue?: boolean;
}): Promise<PriceQuote[]> => {
  try {
    const query: Record<string, string> = {};
    if (params?.status) query.status = params.status;
    if (params?.includeDismissed) query.includeDismissed = "true";
    if (params?.includeLineItems) query.includeLineItems = "true";
    if (params?.workQueue) query.workQueue = "true";
    if (params?.rfqId != null && params.rfqId > 0) {
      query.rfqId = String(params.rfqId);
    }
    const response = await http.get(PRICE_QUOTE_API_URL, { params: query });
    return response.data.priceQuotes ?? [];
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to fetch price quotes");
    }
    throw error;
  }
};

export const getPriceQuote = async (id: number): Promise<PriceQuote> => {
  try {
    const response = await http.get(`${PRICE_QUOTE_API_URL}/${id}`);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data || "Failed to fetch price quote");
    }
    throw error;
  }
};

export const updatePriceQuoteStatus = async (
  id: number,
  status: PriceQuoteStatus
): Promise<PriceQuote> => {
  try {
    const response = await http.patch(`${PRICE_QUOTE_API_URL}/${id}/status`, {
      status,
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to update price quote status"
      );
    }
    throw error;
  }
};

export const getInboundDraftPurchaseOrders = async (params?: {
  rfqId?: number;
}): Promise<PurchaseOrder[]> => {
  try {
    const query: Record<string, string> = {};
    if (params?.rfqId != null && params.rfqId > 0) {
      query.rfqId = String(params.rfqId);
    }
    const response = await http.get(
      `${PURCHASE_ORDER_API_URL}/inbound-drafts`,
      { params: query }
    );
    return response.data.purchaseOrders ?? [];
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to fetch inbound purchase orders"
      );
    }
    throw error;
  }
};

export const getPurchaseOrders = async (params?: {
  status?: string;
  rfqId?: number;
  customerId?: number;
  inboundOnly?: boolean;
  includeItems?: boolean;
  /** Slim columns + recent window for work-queue badge polls. */
  workQueue?: boolean;
}): Promise<PurchaseOrder[]> => {
  try {
    const query: Record<string, string> = {};
    if (params?.status) query.status = params.status;
    if (params?.rfqId != null) query.rfqId = String(params.rfqId);
    if (params?.customerId != null) query.customerId = String(params.customerId);
    if (params?.inboundOnly) query.inboundOnly = "true";
    if (params?.includeItems) query.includeItems = "true";
    if (params?.workQueue) query.workQueue = "true";
    const response = await http.get(PURCHASE_ORDER_API_URL, { params: query });
    return response.data.purchaseOrders ?? [];
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to fetch purchase orders"
      );
    }
    throw error;
  }
};

export const getPurchaseOrder = async (
  id: number,
  options?: { includeConflicts?: boolean }
): Promise<PurchaseOrder> => {
  try {
    const response = await http.get(`${PURCHASE_ORDER_API_URL}/${id}`, {
      params: options?.includeConflicts
        ? { includeConflicts: "true" }
        : undefined,
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to fetch purchase order"
      );
    }
    throw error;
  }
};

export const getPurchaseOrderConflicts = async (
  id: number
): Promise<PurchaseOrderFieldConflict[]> => {
  try {
    const response = await http.get(
      `${PURCHASE_ORDER_API_URL}/${id}/conflicts`
    );
    return response.data.conflicts ?? [];
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to fetch purchase order conflicts"
      );
    }
    throw error;
  }
};

export type PurchaseOrderHistoryEntry = {
  id: number;
  createdAt: string | Date;
  rfqId: number;
  assignedById: number;
  assignedByName: string | null;
  description: string;
  metadata: Array<{
    field: string;
    label?: string;
    oldValue: string | null;
    newValue: string | null;
    source?: string | null;
    action?: string | null;
    poId?: number | null;
    poNum?: string | null;
    undone?: boolean | null;
    undoneAt?: string | null;
    undoneById?: number | null;
  }> | null;
};

export const getPurchaseOrderHistory = async (
  id: number
): Promise<PurchaseOrderHistoryEntry[]> => {
  try {
    const response = await http.get(`${PURCHASE_ORDER_API_URL}/${id}/history`);
    return response.data.history ?? [];
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to fetch purchase order history"
      );
    }
    throw error;
  }
};

export const applyPurchaseOrderResolutions = async (
  id: number,
  resolutions: PurchaseOrderResolutionChoice[]
): Promise<PurchaseOrder> => {
  try {
    const response = await http.post(
      `${PURCHASE_ORDER_API_URL}/${id}/apply`,
      { resolutions }
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to apply purchase order"
      );
    }
    throw error;
  }
};

export const dismissPurchaseOrder = async (
  id: number
): Promise<PurchaseOrder> => {
  try {
    const response = await http.post(
      `${PURCHASE_ORDER_API_URL}/${id}/dismiss`
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to dismiss purchase order"
      );
    }
    throw error;
  }
};

export const updatePurchaseOrderLinkedQuotes = async (
  id: number,
  quoteIds: number[]
): Promise<{
  purchaseOrder: PurchaseOrder;
  conflicts: PurchaseOrderFieldConflict[];
}> => {
  try {
    const response = await http.patch(
      `${PURCHASE_ORDER_API_URL}/${id}/linked-quotes`,
      { quoteIds }
    );
    return {
      purchaseOrder: response.data.purchaseOrder,
      conflicts: response.data.conflicts ?? [],
    };
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to update linked RFQs"
      );
    }
    throw error;
  }
};

export const reanalyzePurchaseOrder = async (
  id: number,
  emailId: number
): Promise<{
  purchaseOrder: PurchaseOrder;
  conflicts: PurchaseOrderFieldConflict[];
}> => {
  try {
    const response = await http.post(
      `${PURCHASE_ORDER_API_URL}/${id}/reanalyze`,
      { emailId }
    );
    return {
      purchaseOrder: response.data.purchaseOrder,
      conflicts: response.data.conflicts ?? [],
    };
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to reanalyze purchase order"
      );
    }
    throw error;
  }
};

export const SAM_GOV_API_URL = `${API_BASE_URL}/sam-gov`;

export const searchSamGovOpportunities = async (
  params: SamGovSearchParams
): Promise<SamGovSearchResponse> => {
  try {
    const response = await http.get<SamGovSearchResponse>(
      `${SAM_GOV_API_URL}/opportunities`,
      { params }
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to search SAM.gov opportunities"
      );
    }
    throw error;
  }
};

export const importSamGovOpportunity = async (
  opportunity: SamGovOpportunity
): Promise<Rfq> => {
  try {
    const response = await http.post<Rfq>(
      `${SAM_GOV_API_URL}/opportunities/import`,
      {
        noticeId: opportunity.noticeId,
        title: opportunity.title,
        solicitationNumber: opportunity.solicitationNumber,
        fullParentPathName: opportunity.fullParentPathName,
        postedDate: opportunity.postedDate,
        responseDeadLine: opportunity.responseDeadLine,
        naicsCode: opportunity.naicsCode,
        classificationCode: opportunity.classificationCode,
        typeOfSetAside: opportunity.typeOfSetAside,
        typeOfSetAsideDescription: opportunity.typeOfSetAsideDescription,
        uiLink: opportunity.uiLink,
        resourceLinks: opportunity.resourceLinks,
        pointOfContact: opportunity.pointOfContact,
        priority: 1,
      }
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to import SAM.gov opportunity"
      );
    }
    throw error;
  }
};

export const getSamGovNoticeDescription = async (
  noticeId: string
): Promise<{ noticeId: string; description: string }> => {
  try {
    const response = await http.get<{ noticeId: string; description: string }>(
      `${SAM_GOV_API_URL}/opportunities/${encodeURIComponent(noticeId)}/description`
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to fetch SAM.gov notice description"
      );
    }
    throw error;
  }
};

export const getSamGovDismissedNoticeIds = async (): Promise<string[]> => {
  try {
    const response = await http.get<{ noticeIds: string[] }>(
      `${SAM_GOV_API_URL}/dismissed`
    );
    return response.data.noticeIds ?? [];
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to load dismissed SAM.gov notices"
      );
    }
    throw error;
  }
};

export const dismissSamGovNotices = async (
  noticeIds: string[]
): Promise<string[]> => {
  try {
    const response = await http.post<{ noticeIds: string[] }>(
      `${SAM_GOV_API_URL}/dismissed`,
      { noticeIds }
    );
    return response.data.noticeIds ?? [];
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data || "Failed to dismiss SAM.gov notices"
      );
    }
    throw error;
  }
};

// Backward-compatible aliases
export const addVendorFee = addFee;
export const updateVendorFee = updateFee;
export const deleteVendorFee = deleteFee;

export interface CompanyDocumentMeta {
  id: number;
  docKey: string;
  fileName: string;
  contentType: string;
  certificateYear: string;
  updatedById: number | null;
  updatedAt: string;
}

export interface CompanyDocumentWithContent extends CompanyDocumentMeta {
  contentBase64: string;
}

const COMPANY_DOCUMENT_API_URL = `${API_BASE_URL}/company-document`;

export const getTaxCertificateMeta = async (): Promise<CompanyDocumentMeta> => {
  try {
    const response = await http.get<CompanyDocumentMeta>(
      `${COMPANY_DOCUMENT_API_URL}/tax-certificate`
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        axiosErrorToMessage(error) || "Failed to load tax certificate"
      );
    }
    throw error;
  }
};

export const getTaxCertificateContent =
  async (): Promise<CompanyDocumentWithContent> => {
    try {
      const response = await http.get<CompanyDocumentWithContent>(
        `${COMPANY_DOCUMENT_API_URL}/tax-certificate/content`
      );
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(
          axiosErrorToMessage(error) || "Failed to load tax certificate file"
        );
      }
      throw error;
    }
  };

export const uploadTaxCertificate = async (params: {
  fileName: string;
  contentType: string;
  contentBase64: string;
  certificateYear?: string;
}): Promise<CompanyDocumentMeta> => {
  try {
    const response = await http.put<CompanyDocumentMeta>(
      `${COMPANY_DOCUMENT_API_URL}/tax-certificate`,
      params
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        axiosErrorToMessage(error) || "Failed to upload tax certificate"
      );
    }
    throw error;
  }
};

export const updateTaxCertificateYear = async (
  certificateYear: string
): Promise<CompanyDocumentMeta> => {
  try {
    const response = await http.patch<CompanyDocumentMeta>(
      `${COMPANY_DOCUMENT_API_URL}/tax-certificate/year`,
      { certificateYear }
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        axiosErrorToMessage(error) || "Failed to update certificate year"
      );
    }
    throw error;
  }
};

export type RcChatApiRequest = {
  message?: string;
  conversationId?: string;
  pageContext: {
    pathname: string;
    rfqId?: number;
    emailId?: number;
    poId?: number;
    priceQuoteId?: number;
    lastRfqId?: number;
    focusedHomeworkItemId?: number;
    role?: string;
  };
  confirmToken?: string;
  cancelConfirmToken?: string;
};

export type RcChatApiResponse = {
  reply: string;
  clientActions: Array<Record<string, unknown>>;
  pendingConfirmations: Array<{
    confirmToken: string;
    summary: string;
    toolName: string;
  }>;
  conversationId: string;
  invalidate?: string[];
};

export const rcChat = async (
  body: RcChatApiRequest
): Promise<RcChatApiResponse> => {
  try {
    const response = await http.post<RcChatApiResponse>(
      `${API_BASE_URL}/rc/chat`,
      body
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(axiosErrorToMessage(error) || "RC request failed");
    }
    throw error;
  }
};

/** OpenAI TTS for RC (southern farmer voice). Returns MP3 blob. */
export const rcSpeak = async (text: string): Promise<Blob> => {
  try {
    const response = await http.post(
      `${API_BASE_URL}/rc/speak`,
      { text },
      { responseType: "blob" }
    );
    return response.data as Blob;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(axiosErrorToMessage(error) || "RC speech failed");
    }
    throw error;
  }
};

export type PackingSlipCopyTypeApi = "customer" | "rocket_city";

export interface CreatedPackingSlip {
  id: number;
  slipNumber: string;
  rfqId: number;
  poNumber: string;
  lineNumbers: number[];
  copyTypes: string[];
  createdById: number | null;
  createdAt: string;
}

export interface PackingSlipPdfAttachment {
  copyType: PackingSlipCopyTypeApi;
  name: string;
  contentType: string;
  contentBytes: string;
}

export interface CreatePackingSlipResponse {
  packingSlip: CreatedPackingSlip;
  pdfs: PackingSlipPdfAttachment[];
  pdfError?: string;
}

const PACKING_SLIP_API_URL = `${API_BASE_URL}/packing-slip`;
const BILL_OF_LADING_API_URL = `${API_BASE_URL}/bill-of-lading`;

export const createPackingSlip = async (payload: {
  rfqId: number;
  poNumber?: string;
  buyerName?: string;
  lineNumbers: number[];
  copyTypes: PackingSlipCopyTypeApi[];
  slipNumber?: string;
  documents?: Partial<Record<PackingSlipCopyTypeApi, string>>;
}): Promise<CreatePackingSlipResponse> => {
  try {
    const response = await http.post(PACKING_SLIP_API_URL, payload);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        typeof error.response?.data === "string"
          ? error.response.data
          : axiosErrorToMessage(error) || "Failed to create packing slip"
      );
    }
    throw error;
  }
};

export const createBillOfLadingPdf = async (payload: {
  fileName?: string;
  fill?: {
    service: "priority" | "economy";
    date?: string;
    poNumber?: string;
    bolNumber?: string;
    specialInstructions?: string;
    billingFxfAcct?: string;
    keepTemplateConsignee?: boolean;
    shipper: {
      name?: string;
      attn?: string;
      address?: string;
      address2?: string;
      city?: string;
      state?: string;
      zip?: string;
      country?: string;
      areaCode?: string;
      phone?: string;
      email?: string;
      fxfAcct?: string;
    };
    consignee?: {
      name?: string;
      attn?: string;
      address?: string;
      address2?: string;
      city?: string;
      state?: string;
      zip?: string;
      country?: string;
      areaCode?: string;
      phone?: string;
      email?: string;
      fxfAcct?: string;
    };
    lines: Array<{
      handlingUnits?: string;
      handlingUnitPkgType?: string;
      pieces?: string;
      hazardousMaterial?: string;
      description?: string;
      weightLbs?: string;
      nmfcItem?: string;
      freightClass?: string;
      cube?: string;
    }>;
  };
  /** @deprecated Prefer `fill` (FedEx template). */
  html?: string;
}): Promise<{ name: string; contentType: string; contentBytes: string }> => {
  try {
    const response = await http.post(`${BILL_OF_LADING_API_URL}/pdf`, payload);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        typeof error.response?.data === "string"
          ? error.response.data
          : axiosErrorToMessage(error) || "Failed to create Bill of Lading PDF"
      );
    }
    throw error;
  }
};

const MFG_COC_COMPARE_API_URL = `${API_BASE_URL}/mfg-coc-compare`;

export const listMfgCocCompares = async (
  homeworkItemId: number
): Promise<MfgCocCompare[]> => {
  try {
    const response = await http.get<MfgCocCompare[]>(MFG_COC_COMPARE_API_URL, {
      params: { homeworkItemId },
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        axiosErrorToMessage(error) || "Failed to load COC compares"
      );
    }
    throw error;
  }
};

export const getMfgCocCompare = async (
  id: number
): Promise<MfgCocCompare> => {
  try {
    const response = await http.get<MfgCocCompare>(
      `${MFG_COC_COMPARE_API_URL}/${id}`
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        axiosErrorToMessage(error) || "Failed to load COC compare"
      );
    }
    throw error;
  }
};

export const createMfgCocCompare = async (params: {
  homeworkItemId: number;
  fileName: string;
  contentType: string;
  contentBase64: string;
}): Promise<MfgCocCompare> => {
  try {
    const response = await http.post<MfgCocCompare>(
      MFG_COC_COMPARE_API_URL,
      params
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        axiosErrorToMessage(error) || "Failed to run Rocket Compare"
      );
    }
    throw error;
  }
};

export type PodPackagePhotoPayload = {
  photoMimeType?: string;
  photoDataBase64: string;
};

export type PodPackagePayload = {
  lineIndex?: number;
  itemName: string;
  description?: string;
  qty?: number;
  rate?: number;
  /** Single photo (legacy). Prefer `photos` for multiple per line. */
  photoMimeType?: string;
  photoDataBase64?: string;
  /** Multiple photos for this line item (up to 10). */
  photos?: PodPackagePhotoPayload[];
};

export type PodReceiptLinePayload = {
  itemName: string;
  description?: string;
  qty?: number;
  rate?: number;
  lineNumber?: number;
};

export type PodPackageDto = {
  id: number;
  lineIndex: number;
  itemName: string;
  description: string;
  qty: number | null;
  photoMimeType: string;
  photoDataBase64: string;
  capturedAt: string;
};

export type PodReceiptDto = {
  id: number;
  createdByUserId: number | null;
  customerId: number | null;
  customerName: string;
  qbInvoiceRefNumber: string;
  qbInvoiceTxnId: string;
  signerName: string;
  signedAt: string | null;
  signaturePngBase64: string;
  poNumber: string;
  memo: string;
  txnDate: string | null;
  totalAmount: number;
  lines: PodReceiptLinePayload[];
  qbSyncQueueId: number | null;
  status: string;
  packages: PodPackageDto[];
  photos: Array<{
    id: number;
    mimeType: string;
    dataBase64: string;
    caption: string;
    capturedAt: string;
  }>;
  createdAt: string;
  updatedAt: string;
};

export type PodInvoiceCandidate = {
  queueId: number;
  customer: string;
  refNumber: string;
  txnId: string;
  poNumber: string;
  txnDate: string | null;
  lines: Array<{
    itemName: string;
    description?: string;
    qty?: number;
    rate?: number;
  }>;
  syncedAt: string | null;
};

export type CreatePodReceiptResponse = {
  podReceipt: PodReceiptDto;
  qbSyncQueueId: number | null;
};

const POD_RECEIPTS_API_URL = `${API_BASE_URL}/pod-receipts`;

export const listPodInvoices = async (params?: {
  customerId?: number;
  customerName?: string;
  refNumber?: string;
  poNumber?: string;
}): Promise<PodInvoiceCandidate[]> => {
  try {
    const query: Record<string, string> = {};
    if (params?.customerId != null) query.customerId = String(params.customerId);
    if (params?.customerName) query.customerName = params.customerName;
    if (params?.refNumber) query.refNumber = params.refNumber;
    if (params?.poNumber) query.poNumber = params.poNumber;
    const response = await http.get<{ invoices: PodInvoiceCandidate[] }>(
      `${POD_RECEIPTS_API_URL}/invoices`,
      { params: query }
    );
    return response.data.invoices ?? [];
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        typeof error.response?.data === "string"
          ? error.response.data
          : axiosErrorToMessage(error) || "Failed to list invoices"
      );
    }
    throw error;
  }
};

export type PodPoLookupResult = {
  poNumber: string;
  customerId: number | null;
  customerName: string;
  buyerName: string;
  shipToAddress: string;
  lines: Array<{
    lineNumber: number;
    itemName: string;
    description?: string;
    qty?: number;
    rate?: number;
    partNumber?: string;
  }>;
  invoices: PodInvoiceCandidate[];
};

export const lookupPodByPoNumber = async (
  poNumber: string,
  customerId?: number
): Promise<PodPoLookupResult> => {
  try {
    const params: Record<string, string> = { poNumber };
    if (customerId != null && customerId > 0) {
      params.customerId = String(customerId);
    }
    const response = await http.get<PodPoLookupResult>(
      `${POD_RECEIPTS_API_URL}/lookup-po`,
      { params }
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        typeof error.response?.data === "string"
          ? error.response.data
          : axiosErrorToMessage(error) || "Failed to look up PO"
      );
    }
    throw error;
  }
};

export const listPodPoNumbersForCustomer = async (
  customerId: number,
  q?: string
): Promise<string[]> => {
  try {
    const params: Record<string, string> = {
      customerId: String(customerId),
    };
    if (q?.trim()) params.q = q.trim();
    const response = await http.get<{ poNumbers: string[] }>(
      `${POD_RECEIPTS_API_URL}/po-numbers`,
      { params }
    );
    return response.data.poNumbers ?? [];
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        typeof error.response?.data === "string"
          ? error.response.data
          : axiosErrorToMessage(error) || "Failed to load PO numbers"
      );
    }
    throw error;
  }
};

export const createPodReceipt = async (payload: {
  customerId?: number | null;
  customerName?: string;
  poNumber: string;
  qbInvoiceRefNumber?: string;
  qbInvoiceTxnId?: string;
  txnDate?: string | null;
  lines?: PodReceiptLinePayload[];
}): Promise<CreatePodReceiptResponse> => {
  try {
    const response = await http.post<CreatePodReceiptResponse>(
      POD_RECEIPTS_API_URL,
      payload
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        typeof error.response?.data === "string"
          ? error.response.data
          : axiosErrorToMessage(error) || "Failed to create proof of delivery"
      );
    }
    throw error;
  }
};

export const completePodReceipt = async (
  id: number,
  payload: {
    signerName: string;
    signaturePngBase64: string;
    signedAt?: string;
    packages: PodPackagePayload[];
  }
): Promise<CreatePodReceiptResponse> => {
  try {
    const response = await http.post<CreatePodReceiptResponse>(
      `${POD_RECEIPTS_API_URL}/${id}/complete`,
      payload
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        typeof error.response?.data === "string"
          ? error.response.data
          : axiosErrorToMessage(error) || "Failed to complete proof of delivery"
      );
    }
    throw error;
  }
};

export type ListPodReceiptsParams = {
  limit?: number;
  status?: string;
  customerId?: number;
  customerName?: string;
  invoiceNumber?: string;
  poNumber?: string;
  /** Exact invoice date YYYY-MM-DD */
  invoiceDate?: string;
  invoiceDateFrom?: string;
  invoiceDateTo?: string;
  /** Exact dollar amount */
  amount?: string | number;
  amountMin?: string | number;
  amountMax?: string | number;
};

export const getPodReceipt = async (
  id: number
): Promise<PodReceiptDto> => {
  try {
    const response = await http.get<{ podReceipt: PodReceiptDto }>(
      `${POD_RECEIPTS_API_URL}/${id}`
    );
    return response.data.podReceipt;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        typeof error.response?.data === "string"
          ? error.response.data
          : axiosErrorToMessage(error) || "Failed to load proof of delivery"
      );
    }
    throw error;
  }
};

export const listPodReceipts = async (
  params: ListPodReceiptsParams | number = 50
): Promise<PodReceiptDto[]> => {
  try {
    const query: Record<string, string> = {};
    if (typeof params === "number") {
      query.limit = String(params);
    } else {
      if (params.limit != null) query.limit = String(params.limit);
      if (params.status) query.status = params.status;
      if (params.customerId != null) query.customerId = String(params.customerId);
      if (params.customerName) query.customerName = params.customerName;
      if (params.invoiceNumber) query.invoiceNumber = params.invoiceNumber;
      if (params.poNumber) query.poNumber = params.poNumber;
      if (params.invoiceDate) query.invoiceDate = params.invoiceDate;
      if (params.invoiceDateFrom) query.invoiceDateFrom = params.invoiceDateFrom;
      if (params.invoiceDateTo) query.invoiceDateTo = params.invoiceDateTo;
      if (params.amount != null && String(params.amount).trim() !== "") {
        query.amount = String(params.amount);
      }
      if (params.amountMin != null && String(params.amountMin).trim() !== "") {
        query.amountMin = String(params.amountMin);
      }
      if (params.amountMax != null && String(params.amountMax).trim() !== "") {
        query.amountMax = String(params.amountMax);
      }
    }
    const response = await http.get<{ podReceipts: PodReceiptDto[] }>(
      POD_RECEIPTS_API_URL,
      { params: query }
    );
    return response.data.podReceipts;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(
        typeof error.response?.data === "string"
          ? error.response.data
          : axiosErrorToMessage(error) || "Failed to list proofs of delivery"
      );
    }
    throw error;
  }
};


// ---------------------------------------------------------------------------
// Staff chat
// ---------------------------------------------------------------------------

const CHAT_API_URL = `${API_BASE_URL}/chat`;

async function chatRequest<T>(
  request: () => Promise<AxiosResponse<T>>,
  fallback: string
): Promise<T> {
  try {
    const response = await request();
    return response.data;
  } catch (error) {
    throw new Error(apiErrorMessage(error, fallback));
  }
}

export const fetchChatConversations = () =>
  chatRequest<ChatConversation[]>(
    () => http.get(`${CHAT_API_URL}/conversations`),
    "Failed to load conversations"
  );

export const fetchChatConversation = (conversationId: number) =>
  chatRequest<ChatConversation>(
    () => http.get(`${CHAT_API_URL}/conversations/${conversationId}`),
    "Failed to load conversation"
  );

export const createChatConversation = (input: ChatCreateConversationInput) =>
  chatRequest<ChatConversation>(
    () => http.post(`${CHAT_API_URL}/conversations`, input),
    "Failed to create conversation"
  );

export const updateChatConversation = (
  conversationId: number,
  changes: { name?: string; description?: string; archived?: boolean }
) =>
  chatRequest<ChatConversation>(
    () => http.patch(`${CHAT_API_URL}/conversations/${conversationId}`, changes),
    "Failed to update conversation"
  );

export const joinChatConversation = (conversationId: number) =>
  chatRequest<ChatConversation>(
    () => http.post(`${CHAT_API_URL}/conversations/${conversationId}/join`),
    "Failed to join channel"
  );

export const leaveChatConversation = (conversationId: number) =>
  chatRequest<{ left: boolean }>(
    () => http.post(`${CHAT_API_URL}/conversations/${conversationId}/leave`),
    "Failed to leave conversation"
  );

export const addChatMembers = (conversationId: number, userIds: number[]) =>
  chatRequest<ChatConversation>(
    () =>
      http.post(`${CHAT_API_URL}/conversations/${conversationId}/members`, {
        userIds,
      }),
    "Failed to add members"
  );

export const markChatConversationRead = (conversationId: number) =>
  chatRequest<{ read: boolean }>(
    () => http.post(`${CHAT_API_URL}/conversations/${conversationId}/read`),
    "Failed to mark conversation read"
  );

export const fetchChatMessages = (conversationId: number, before?: number) =>
  chatRequest<ChatMessagePage>(
    () =>
      http.get(`${CHAT_API_URL}/conversations/${conversationId}/messages`, {
        params: before ? { before } : undefined,
      }),
    "Failed to load messages"
  );

export const fetchChatThread = (messageId: number) =>
  chatRequest<ChatThread>(
    () => http.get(`${CHAT_API_URL}/messages/${messageId}/replies`),
    "Failed to load thread"
  );

export const sendChatMessage = (
  conversationId: number,
  input: ChatSendMessageInput
) =>
  chatRequest<ChatMessage>(
    () =>
      http.post(`${CHAT_API_URL}/conversations/${conversationId}/messages`, input),
    "Failed to send message"
  );

export const editChatMessage = (
  messageId: number,
  body: string,
  mentionUserIds: number[]
) =>
  chatRequest<ChatMessage>(
    () =>
      http.patch(`${CHAT_API_URL}/messages/${messageId}`, {
        body,
        mentionUserIds,
      }),
    "Failed to edit message"
  );

export const deleteChatMessage = (messageId: number) =>
  chatRequest<ChatMessage>(
    () => http.delete(`${CHAT_API_URL}/messages/${messageId}`),
    "Failed to delete message"
  );

export const addChatReaction = (messageId: number, emoji: string) =>
  chatRequest<ChatMessage>(
    () => http.post(`${CHAT_API_URL}/messages/${messageId}/reactions`, { emoji }),
    "Failed to add reaction"
  );

export const removeChatReaction = (messageId: number, emoji: string) =>
  chatRequest<ChatMessage>(
    () =>
      http.delete(
        `${CHAT_API_URL}/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`
      ),
    "Failed to remove reaction"
  );

export const fetchChatAttachment = (attachmentId: number) =>
  chatRequest<ChatAttachmentContent>(
    () => http.get(`${CHAT_API_URL}/attachments/${attachmentId}`),
    "Failed to download attachment"
  );

// ---------------------------------------------------------------------------
// Microsoft Teams connection (chat sync)
// ---------------------------------------------------------------------------

const TEAMS_API_URL = `${API_BASE_URL}/teams`;

export const fetchTeamsStatus = () =>
  chatRequest<TeamsStatus>(
    () => http.get(`${TEAMS_API_URL}/status`),
    "Failed to load Teams status"
  );

/** Returns the Microsoft sign-in URL; the browser should navigate to it. */
export const startTeamsConnect = (returnTo: string) =>
  chatRequest<{ authorizeUrl: string }>(
    () => http.post(`${TEAMS_API_URL}/connect`, { returnTo }),
    "Failed to start Teams sign-in"
  );

export const disconnectTeamsAccount = () =>
  chatRequest<TeamsStatus>(
    () => http.post(`${TEAMS_API_URL}/disconnect`),
    "Failed to disconnect Teams"
  );
